// Workspace-project private-data loader.
//
// Drives the workspace-project app's data hydration in response to auth
// transitions. Extracted from the app.js root onCreate closure because
// the smbls 3.14 auto-router mounts page DOMQL components directly into
// `Slot` from the shell's pages registry — the workspace-project wrapper
// never instantiates, so its `onCreate` never fires. The shell's
// `subscribeAuthSession` runs these callbacks instead. Kept as plain
// functions (not lifecycle hooks) so either entry point — shell auth
// subscription or sub-app onCreate when run standalone — can invoke them.
//
// `loadPrivateData` runs once per sign-in transition and hydrates
// people/teams/roles/userDocs/userPrefs + financials. `onWorkspaceChange`
// runs when the JWT's `active_workspace_id` flips for the same user
// (org switch); it re-fetches workspace-scoped tables in place + rebinds
// realtime channels so the UI never has to full-page-reload.

// Module-level in-flight guard. JS single-threaded execution model makes
// the check-then-set atomic — no microtask race possible regardless of how
// the caller schedules the invocation (onAuthStateChange callback, getSession
// prime, or direct call from a page onCreate). This closes vector 4 of the
// WORKSPACE-PERF-1 series: _authStateDelivered in auth.js still had a
// microtask-ordering race where initPromise.then(onAuthStateChange) and
// initPromise.then(getSession) resolve in the same tick but onAuthStateChange
// fires through a nested async chain, leaving _authStateDelivered false when
// the getSession prime's check runs and allowing a second loadPrivateData to
// start concurrently. The call-site guards in auth.js are left in place for
// defense-in-depth — they cost nothing and catch races at the dispatch layer.
let _shellLoadInFlight = null
let _bootLoadGeneration = 0
let _bootRevealBudgetMs = 7000
export const __setBootRevealBudgetForTests = (ms) => {
  _bootRevealBudgetMs = ms
}
// Scope fingerprint captured when the running pass started + a one-slot rerun
// queue. A changed scope needs a corrected pass, and a same-scope replacement
// root needs its OWN hydration. Retain the latest caller and its loader epoch;
// a newer workspace switch or reveal owner cancels that pending handoff.
let _loadScopeAtStart = null
let _loadRerunQueued = null

// _dataLoading OWNERSHIP EPOCH (BOOT-RESHUFFLE F3). The flag is raised by three
// independent drivers — boot loadPrivateData, the org/workspace-switch handlers
// (app.js), and onWorkspaceChange — but was cleared unconditionally by
// whichever finished first: the boot pass's `finally` routinely dropped the
// MainLoader in the middle of an org-switch teardown, revealing half-wiped UI
// (the "multiple refreshes" landing). Every raise bumps the epoch; a clear is
// honored only while its epoch is still the newest raise. The inverse wedge
// (supersession early-returns leaving the flag true forever, wedging the home
// grid's 120s init gate) is covered by the same invariant: whichever run
// raised last is the one whose clear path runs.
let _dataLoadEpoch = 0
let _dataLoadClearedFor = 0
// Queue acceptance needs to distinguish a switch on the old shell from a
// switch that already owns the queued root. Weak keys retain no removed roots.
// Clears still use the existing global epoch contract below.
const _dataLoadEpochByRoot = new WeakMap()
export const beginDataLoad = (root, patch = {}) => {
  const epoch = ++_dataLoadEpoch
  try {
    _dataLoadEpochByRoot.set(root, epoch)
    root.update({ _dataLoading: true, ...patch })
  } catch (_) {
    /* torn down mid-raise */
  }
  return epoch
}
export const endDataLoad = (root, epoch, patch = {}) => {
  if (epoch !== _dataLoadEpoch) return false
  if (_dataLoadClearedFor === epoch) {
    // Idempotent per epoch — a safety-net clear after the real reveal must not
    // re-dispatch `_dataLoading:false`; still deliver any patch keys.
    if (Object.keys(patch).length) {
      try {
        root.update(patch)
      } catch (_) {}
    }
    return true
  }
  _dataLoadClearedFor = epoch
  try {
    root.update({ _dataLoading: false, ...patch })
  } catch (_) {
    /* torn down mid-clear */
  }
  return true
}

// A first sign-in can receive the SDK session before the workspace claim/list
// arrives. auth.js owns the short post-claim module/apps/config + dashboard-prefs
// reconciliation for that case. Keep it in the SAME loader epoch as the
// initial private-data pass, so the shell never reveals a default workspace and
// then visibly repaints it once the real workspace arrives.
//
// The marker is module-private (rather than another reactive root key): it is
// coordination metadata, not user-visible state. The full-record listener
// below uses it to avoid launching a competing config-converge pass while the
// post-auth reconciliation already owns that exact workspace.
//
// PLAIN top-level functions + a module-scope WeakMap, NOT the coordinator
// factory's bound closure. `createPostAuthWorkspaceRevealCoordinator` (below,
// still exported for `initialWorkspaceReveal.test.js`'s dependency-injected
// unit test) returns `begin`/`isActive`/`end` closures over ITS OWN
// `beginDataLoad`/`endDataLoad` parameters — and once those closures are
// individually reachable (`beginPostAuthWorkspaceReveal` etc., referenced from
// auth.js's `subscribeAuthSession`), frank auto-lifts them into `globalScope`
// standalone. A lifted closure cannot resolve a factory PARAMETER at revival —
// renaming the parameter (`_beginDataLoad`/`_endDataLoad`, tried previously)
// only changes which name fails to resolve, not whether it resolves. Measured
// on the current gate: `globalScope.beginPostAuthWorkspaceReveal` /
// `globalScope.endPostAuthWorkspaceReveal` both threw ReferenceError on
// every first-sign-in reveal at a frank-served runtime. Calling this file's
// OWN top-level `beginDataLoad`/`endDataLoad` directly (same identifiers
// `onWorkspaceChange` already calls successfully, no injection) removes the
// captured-parameter free variable entirely.
const _postAuthWorkspaceRevealPending = new WeakMap()
const _dashboardPrefsReads = new WeakMap()

export const beginPostAuthWorkspaceReveal = (root, workspaceId) => {
  if (!root || !workspaceId) return null
  const id = String(workspaceId)
  const held = _postAuthWorkspaceRevealPending.get(root)
  if (held && held.workspaceId === id) return held
  const token = { workspaceId: id, epoch: beginDataLoad(root), timer: null }
  _postAuthWorkspaceRevealPending.set(root, token)
  updateWorkspaceActivation(root, 'reconcile', { state: 'loading' })
  token.timer = setTimeout(() => {
    if (_postAuthWorkspaceRevealPending.get(root) !== token || token.epoch !== _dataLoadEpoch)
      return
    // This raise is still the NEWEST, so nobody else can clear the flag: an org
    // switch that moved the workspace meanwhile raised EARLIER and had its own
    // clear refused. Release our claim whatever workspace is current — the old
    // early return left the loader up for good (dev 2026-09-29, /aihouse/site
    // at 390 px). Only the "still loading" notice is scoped to the workspace
    // this reveal was for.
    if (String(root.workspace?.id || root.workspace?._id || '') === id) {
      updateWorkspaceActivation(root, 'reconcile', { state: 'slow' })
    }
    endDataLoad(root, token.epoch)
  }, _bootRevealBudgetMs)
  token.timer?.unref?.()
  return token
}

export const isPostAuthWorkspaceReveal = (root, workspaceId) => {
  const held = root && _postAuthWorkspaceRevealPending.get(root)
  return !!held && held.workspaceId === String(workspaceId || '')
}

export const endPostAuthWorkspaceReveal = (root, token) => {
  if (!root || !token || _postAuthWorkspaceRevealPending.get(root) !== token) return false
  _postAuthWorkspaceRevealPending.delete(root)
  clearTimeout(token.timer)
  if (String(root.workspace?.id || root.workspace?._id || '') === token.workspaceId) {
    updateWorkspaceActivation(root, 'reconcile', { state: 'ready' })
  }
  return endDataLoad(root, token.epoch)
}
export { awaitInitialWorkspace, hasAuthoritativeWorkspace }
// Failsafe for callers that awaited a pipeline which THREW before its reveal
// (they don't know the epoch): clears whatever raise is current so the loader
// can never wedge on an exception path.
export const endDataLoadFailsafe = (root) => endDataLoad(root, _dataLoadEpoch)

// Boot-phase telemetry — performance marks/measures so a slow boot NAMES its
// slow phase instead of being one opaque 30s spiral. Read them in the console
// (or a QA run) via performance.getEntriesByType('measure') filtered on
// 'wsboot:'. Guarded: no-ops wherever the Performance API is absent.
const _mark = (name) => {
  try {
    if (typeof performance !== 'undefined') performance.mark(name)
  } catch (_) {
    /* telemetry only */
  }
}
const _measure = (name, from, to) => {
  try {
    if (typeof performance !== 'undefined') performance.measure(name, from, to)
  } catch (_) {
    /* telemetry only */
  }
}

// Supersession guard for onWorkspaceChange. Switches are fire-and-forget AND
// dual-triggered (sdk.orgSwitched rootBus + the auth subscription's
// active_workspace_id change), each running a multi-second async refetch.
// Without a generation guard an OLDER switch's late writes land after a NEWER
// switch and win — the "switched to B, still showing A" bug. Every run captures
// a generation; once a newer run starts, the older run stops writing and cedes
// the loader.
let _wsChangeGen = 0

// FABLE P1 (2026-08-10) — `_dataLoading` permanently stuck true after a
// cross-org switch, root-caused. onWorkspaceChange's own pipeline awaits a
// chain of real network calls (getWorkspaceCached → sdk.getWorkspace,
// listOrgMembers, loadAllTeams, loadWorkspaceProject's settings fetch, the
// dashboard-prefs batch) with NO per-call timeout anywhere in that chain. A
// request that never settles (a stalled/queued round-trip — this dev
// environment's shared checkout routinely starves the browser's ~6-connection
// HTTP/1.1 pool across concurrent tabs/probes, see
// project_two_tab_localhost_connection_starvation.md — or any genuinely wedged
// backend call) parks the `await` forever: no throw, so the surrounding
// try/catch never fires and `endDataLoad` (the ONLY place this pipeline clears
// the flag, at the reveal below) is simply never reached. Reproduced
// identically on two unrelated orgs (not org-specific) — consistent with a
// generic unguarded-await defect rather than a per-tenant data issue.
//
// The UI org-switcher's OWN 12s "genuinely stuck" reload fallback
// (AppOrgSwitcher.js `_wsFallbackTimer`) does NOT cover this: it is
// disarmed by `clearWorkspaceSwitchFallback()` on onWorkspaceChange's very
// FIRST line — the instant the pipeline STARTS, not when it FINISHES — so a
// stall anywhere inside the pipeline (which is exactly where this bug lives)
// runs completely unguarded. It also only exists for the org-switcher's own
// commit flow — the auth-subscription's direct claims-change trigger and
// programmatic `sdk.switchOrg`/`switchWorkspace` callers (AI tools, cross-tab
// convergence, tests) invoke `onWorkspaceChange` with no fallback timer at
// all.
//
// Fix: a per-run watchdog scoped to THIS run's own `_dataLoading` epoch
// (armed right after `beginDataLoad`, self-cancelling the instant a newer
// switch supersedes the epoch or this run reaches its own reveal). It doesn't
// try to un-stick the underlying stalled request — there is no generic way to
// abort an arbitrary in-flight SDK call from here — it guarantees the LOADER
// itself always resolves within a bounded window either way: converge (the
// normal path) or fail loud + release (this path), never a silent infinite
// spinner. Set comfortably under the org-switcher's 12s reload fallback so
// the UI-driven flow gets a clean error+release instead of a jarring full
// reload; kept as a named export so tests can shrink it.
let _wsChangeWatchdogMs = 10000
export const __setWorkspaceChangeWatchdogMsForTests = (ms) => {
  _wsChangeWatchdogMs = ms
}

// Bound on the LiveKit teardown the switch awaits before its PHASE-1 wipe
// (MEET-LIVE-CALL-SURVIVES-SWITCH-1). A `room.disconnect()` normally settles in
// well under a second; this only exists so a signal socket that never answers
// cannot hold the PREVIOUS tenant's data on screen — past the bound the wipe
// proceeds and the failure is logged loudly, never swallowed. Comfortably under
// the switch watchdog above so the two can never race for the same symptom.
// Named export so tests can shrink it.
let _livekitTeardownBoundMs = 2000
export const __setLivekitTeardownBoundMsForTests = (ms) => {
  _livekitTeardownBoundMs = ms
}

// ── Switch-pipeline phase instrumentation + reveal budget ───────────────────
//
// WS-SWITCH-ONWORKSPACECHANGE-BOUNDED-WINDOW-TRIP-1. The watchdog above tells
// us the pipeline did not settle, but never WHICH await stalled — a live
// cross-org switch (Symbols System → Bellforge, dev) tripped it with no way to
// name the offender. Every pre-reveal await is now run through `_phase`,
// which:
//   1. stamps its duration (performance.now) into a per-run table published on
//      `root.__wsSwitchPhases` — readable live from the console/Chrome MCP,
//      and NOT a state key (no `root.update`), so instrumentation can never
//      repaint the UI or race the epoch-guarded writes;
//   2. BOUNDS the await against a single shared REVEAL BUDGET. Per-phase
//      timeouts alone do not bound the pipeline (n phases × n bounds can still
//      exceed the watchdog); one budget shared by all phases guarantees the
//      reveal is reached inside the watchdog window on ANY stall.
// A bounded-out phase is NON-FATAL: the pass continues with its fallback value
// and the underlying promise keeps running (a slow module still applies when
// it lands, see the `loadWorkspaceProject` continuation below). The watchdog
// stays as the safety net for a stall OUTSIDE these phases — and now prints
// the phase table when it fires.
//
// Budget sits well under `_wsChangeWatchdogMs` so a healthy-but-slow switch
// reveals instead of tripping the loud error (the live symptom: a cold parcel
// module chunk on dev takes longer than the whole window).
let _switchRevealBudgetMs = 7000
export const __setSwitchRevealBudgetMsForTests = (ms) => {
  _switchRevealBudgetMs = ms
}
// Verbose per-phase logging is opt-in (`root.__wsSwitchDebug = true` in the
// console). A phase that BOUNDS OUT is always logged — that is the finding the
// ticket exists for and it must never need a flag to be visible.
// Sentinel resolved by a phase's bound — never a legal phase VALUE.
const _BOUNDED = Symbol('ws-switch-phase-bounded')
const _nowMs = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

// Opaque tenant token for the @symbo.ls/fetch cache + dedup keys (read by the
// plugin's resolveScope off root.__fetchScope). Isolates every declarative
// fetch cache entry per org+workspace so a page mounted right after a switch
// never serves the previous tenant's cached rows.
const _fetchScope = (root, sdk) => {
  // org id: bootShell seeds root.org / jwtClaims.workspace_org_id but does NOT
  // populate sdk._context.activeOrgId until an explicit switchOrg, so prefer
  // root — otherwise the token is empty at boot and never isolates orgs.
  const org = root?.org?.id || root?.jwtClaims?.workspace_org_id || sdk?._context?.activeOrgId || ''
  // ws id: switchWorkspace writes sdk._context.activeWorkspaceId (authoritative);
  // fall back to the JWT claim / resolved workspace record.
  const ws =
    sdk?._context?.activeWorkspaceId ||
    root?.jwtClaims?.active_workspace_id ||
    root?.activeWorkspaceId ||
    root?.workspace?.id ||
    root?.workspace?._id ||
    ''
  return `${org}.${ws}`
}

import { initials } from './index.js'
import {
  readStoredAssistantDock,
  reconcileAssistantDockFromPrefs,
  setAssistantDock
} from './assistantDock.js'
import {
  readStoredAssistantMode,
  reconcileAssistantModeFromPrefs,
  setAssistantMode
} from './assistantMode.js'
import { readStoredAgentMode, reconcileAgentModeFromPrefs, setAgentMode } from './agentMode.js'
import {
  readStoredAssistantWidth,
  reconcileAssistantWidthFromPrefs,
  setAssistantWidth
} from './assistantWidth.js'
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'
const shellGetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.getActiveWorkspacePointer(...args)
const shellSetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.setActiveWorkspacePointer(...args)
// The comm host (docs/COMM_HOST.md) never shows the home dashboard or the
// documents list — their reads are skipped there.
const shellIsCommHost = () => _wsShared.functions.isCommHost()
const shellListOrgMembers = (...args) => _wsShared.functions.listOrgMembers(...args)
const invalidateOrgMembers = (...args) => _wsShared.functions.invalidateOrgMembers(...args)
const shellSetActiveWorkspaceRecord = (...args) => _wsShared.functions.setActiveWorkspaceRecord(...args)
// Theme-mode record helpers (shared/functions/themeMode.js) — same
// `_wsShared.functions.*` indirection as the workspace-pointer helpers above.
const reconcileThemeModeFromPrefs = (...args) =>
  _wsShared.functions.reconcileThemeModeFromPrefs(...args)
const persistThemeModeToUser = (...args) => _wsShared.functions.persistThemeModeToUser(...args)
const shellReadStoredThemeMode = (...args) => _wsShared.functions.readStoredThemeMode(...args)
const shellReadTenantBootThemeMode = (...args) => _wsShared.functions.readTenantBootThemeMode?.(...args)
const shellWorkspaceThemeModeOf = (...args) => _wsShared.functions.workspaceThemeModeOf?.(...args)
const shellWriteStoredThemeMode = (...args) => _wsShared.functions.writeStoredThemeMode(...args)
const shellStampWorkspaceShape = (...args) => _wsShared.functions.stampWorkspaceShape(...args)
const onActiveWorkspaceFull = (...args) => _wsShared.functions.onActiveWorkspaceFull(...args)
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)
const shellClearWorkspaceSwitchFallback = (...args) =>
  _wsShared.components.clearWorkspaceSwitchFallback(...args)
// Deferred to call time — an eager module-scope destructure of the barrel's
// default crashes the shell boot inside the shared-barrel init cycle.
const shellGetWorkspaceRawSegments = (...args) => _wsShared.functions.getWorkspaceRawSegments(...args)
const shellGetWorkspaceQueryParam = (...args) => _wsShared.functions.getWorkspaceQueryParam(...args)
// PERSONA-5 — purge the sessionStorage persona blob (personaSession.js,
// PERSONA-2) in lockstep with the state wipe below. `_personaSession` is
// registered in WORKSPACE_SCOPED_KEYS so `workspaceScopedWipe()` nulls the
// STATE reflection, but that state key was never derived FROM the
// sessionStorage blob (nothing calls `readPersonaSession()` — the pill
// restores from the server oracle instead), so the blob is a second,
// independent copy that the state wipe alone never touches. Left alone it
// would keep naming the OLD workspace after the switch — harmless for auth
// (readPersonaWorkspaceToken's own workspace-mismatch guard refuses it, and
// the SDK already ended the session server-side via PersonaService's
// switchWorkspace/switchOrg hooks by the time this runs) but exactly the
// stale-second-copy hazard the isolation registry exists to close.
const shellClearPersonaSession = (...args) => _wsShared.functions.clearPersonaSession(...args)
import { awaitInitialWorkspace, hasAuthoritativeWorkspace } from './initialWorkspaceReveal.js'
import { workspaceScopedWipe } from '../scopes.js'
import { writeBootSnapshot, purgeBootSnapshot } from './bootSnapshot.js'
import { resolveWorkspaceSkin } from './workspaceSkin.js'
import { isCustomDomainHost } from './customDomainHost.js'
import { consumePrimedPeople, primedOrgSlug } from './peoplePrime.js'
import { applyWorkspaceConfig } from './workspaceConfigs.js'
import {
  loadWorkspaceProject,
  loadWorkspaceApps,
  whenAppRouteReady,
  updateWorkspaceActivation,
  settleWorkspaceActivation
} from './workspaceProjects.js'
import { healUserRecord } from './healUserRecord.js'
import { getWorkspaceCached as _gwcDirect } from './getWorkspaceCached.js'
import { loadCreditGuard } from './creditGuard.js'
// WS-HISTORY-PAGE-STAYS-LOADING-AFTER-WORKSPACE-SWITCH-1 — the History page's
// own log read, re-run by the switch tail below (the `historySurfaceActive` pair).
import { isHistoryPath, loadHistoryEvents } from './history.js'
// POB S9 — the Getting started → Team page's own read, re-run by the switch
// tail below (the `gettingStartedSurfaceActive` pair), the History way.
import { gettingStartedSurfaceFor, loadGettingStartedTeam } from './gettingStartedTeam.js'
// POB S10 — the Getting started → Imports page's own read, the same pair
// (`gettingStartedImportsActive`).
import { loadGettingStartedImports } from './gettingStartedImports.js'
import { loadAllTeams, loadAllTeamMembers } from '@symbo-ls/workspace-shared/functions/teams.js'
// PROFILE-PAGE-NO-REHYDRATE-ON-SWITCH-1 — the profile surface's own resolver +
// loaders, exported from the shared library so this switch can re-run exactly
// what the page runs on mount. Same cross-package `functions/` import shape as
// teams.js above.
import {
  hydrateUserProfile,
  isUserProfilePath
} from '@symbo-ls/workspace-shared/functions/userProfileData.js'
import { fetchOrgNotifCount } from './notifications/fetchOrgNotifCount.js'
import { setAnalyzingWorkspace, resolveAnalyzingScopeId } from '../analyzing.js'
import { fetchTicketsAssignedTo } from '@symbo-ls/workspace-tickets/functions/ticketLoaders.js'
import { fetchActivityFeed } from '@symbo-ls/workspace-tickets/functions/fetchActivityFeed.js'
import {
  rowToChannel,
  rowToMessage,
  filterChannelsToActiveWorkspace,
  getActiveWorkspaceId,
  scopeMembersToActiveWorkspace,
  rowInActiveWorkspace,
  sortMessagesByTime
} from '@symbo-ls/workspace-chat/functions/chatUtils.js'

// Exported so the shell onCreate (app.js) + the entry prime can map the raw
// /users/members rows into the same person shape this loader uses.
export const _person = (r) => ({
  userId: r.user_id,
  email: r.email,
  // No invented display name: a nameless member reads as their email here,
  // and each render site owns its own translated fallback
  // (`workspace.person.unknown`) for a row with neither.
  name: r.name || r.email || '',
  // avatar carries a URL or NOTHING — never letters. The old
  // falling back to initials(r.name) overloaded one field with URL-or-letters,
  // and every hide-guarded <img> still FETCHED the letters: `hide:` is
  // display:none only (css-in-props block.js), and a browser requests the
  // src of a hidden image — so /N-style paths 404ed on every page for every
  // person without a photo (measured on app.bellforge.eu + the shell,
  // 2026-09-01). Letters live in their own `initials` field; consumers
  // branch on two honest fields, so AVATAR-URL-LEAK-1 stays fixed too
  // (nothing paints a URL as text, nothing fetches letters). A STORED value
  // that is not a URL (letters an older client saved: dev 2026-10-18, "NT"
  // fetched /w/system/NT from a tasks card) reads as nothing too.
  avatar: typeof r.avatar === 'string' && /^(https?:|\/)/.test(r.avatar) ? r.avatar : '',
  initials: initials(r.name),
  position: r.position,
  location: r.location,
  // city/country are the canonical split of the legacy `location` string
  // (migration 0145). MemberProfile's _countryOf/_cityOf read person.country/
  // person.city first; carry them so a saved Country/City survives a shell
  // re-hydrate without falling back to parsing `location`.
  country: r.country,
  city: r.city,
  timezone: r.timezone,
  github: r.github || r.github_username,
  linkedin: r.linkedin,
  twitter: r.twitter,
  birthday: r.birthday,
  start_date: r.start_date,
  end_date: r.end_date,
  status: r.status,
  bio: r.bio,
  pronouns: r.pronouns,
  startDate: r.start_date,
  endDate: r.end_date
})

const filterToActiveOrg = (peopleAll, root) => {
  const orgMembersByOrgId = root?.orgMembersByOrgId || {}
  const activeOrgId = root?.org?.id || root?.jwtClaims?.workspace_org_id
  const memberSet = activeOrgId ? orgMembersByOrgId[activeOrgId] : null
  if (memberSet && typeof memberSet === 'object') {
    const set = memberSet instanceof Set ? memberSet : new Set(Object.keys(memberSet))
    if (set.size > 0) {
      const lower = new Set(Array.from(set).map((e) => String(e || '').toLowerCase()))
      return peopleAll.filter((p) => lower.has(String(p?.email || '').toLowerCase()))
    }
  }
  // No usable member set for the active org — return the FULL list unchanged.
  //
  // `peopleAll` is `users.members`, which is ALREADY org-scoped server-side
  // (see the call site comment near root.update({ peopleAll, people })), so the
  // whole list IS the active-org roster — there's nothing to narrow. The old
  // self-only fallback was actively harmful: orgMembersByOrgId is keyed by the
  // org ids loadAllTeams iterated, which don't always match `activeOrgId`
  // (jwtClaims.workspace_org_id ↔ org.id are different id-spaces per org), so
  // this branch fired on a normal boot and COLLAPSED root.people from the full
  // roster down to just the current user (or [] before currentUserEmail
  // lands). loadPrivateData sets people=full first, then loadAllTeams().then()
  // re-filtered through here and shrank it — which is exactly the home greeting
  // flicker "Good morning nikoloza → there → nikoloza" (findMe → root.people
  // empties then refills) and the "assignee picker shows only me" bug. Never
  // shrink a populated roster on a missing/mismatched member set.
  return peopleAll
}

// sdk.listOrgMembers(orgId) (OrganizationService, GET /organizations/:orgId/
// members — any org member can call it) returns the raw OrgMember docs
// ({ _id, user: { email, ... }, role, effectiveRole }) in one round trip, so
// this builds BOTH the role-by-user map AND the OrgMember._id-by-user map
// (`byUserMemberId` — what `sdk.updateOrgMember(orgId, memberId, { role })`
// PATCHes) from a single fetch. Replaces the former sdk.getOrgMemberRoles
// call (`{email, role}` only, no member id) now that a write path
// (PersonCard's RoleManagerRow) needs the member id too.
//
// It lands the map on EVERY exit, with the version moved — a read that threw
// and a root with no org to read included; a failed read keeps the map it had
// (empty on a fresh boot or switch). The admin route gates WAIT for the map
// (roleHelpers `shouldRedirectNoPerm` reads `roles.version`), so an exit that
// wrote nothing kept them waiting for good: a person with no admin role kept
// the page. Exported for functions/roleMapGate.test.js; the functions barrel
// imports this file by name, so it is not a registered function.
export const _loadRoles = async (root, sdk) => {
  const land = (maps) => {
    const prev = root.roles || { byUser: {}, byUserMemberId: {}, byUserMongoId: {}, version: 0 }
    root.update({ roles: { ...prev, ...maps, version: (prev.version || 0) + 1 } })
  }
  try {
    const orgId = root?.jwtClaims?.workspace_org_id
    if (!orgId) return land({})
    const rows = (await sdk.listOrgMembers(orgId).catch(() => null)) || []
    const emailToUserId = {}
    for (const p of root.peopleAll || []) {
      if (p.email && p.userId) emailToUserId[p.email.toLowerCase()] = p.userId
    }
    const byUser = {}
    const byUserMemberId = {}
    const byUserMongoId = {}
    for (const m of rows) {
      const email = (m.user && m.user.email) || m.email
      const uid = emailToUserId[(email || '').toLowerCase()]
      if (!uid) continue
      byUser[uid] = m.role
      byUserMemberId[uid] = m._id || m.id
      if (m.user && (m.user._id || m.user.id)) {
        byUserMongoId[uid] = m.user._id || m.user.id
      }
    }
    land({ byUser, byUserMemberId, byUserMongoId })
  } catch (e) {
    console.warn('[workspace-project] member-roles fetch failed', e)
    try {
      land({})
    } catch (_) {
      /* the root cannot take the write — nothing left to land it on */
    }
  }
}

// The per-workspace dashboard-prefs load — home prefs (userDashboardPrefs).
// Identical at initial boot and on every workspace switch (dashboard prefs are
// per-workspace, so they MUST re-scope on a switch), so both fan-outs spread
// this in. `tag` labels the two apart in console warnings.
//
// `home_default_panels` used to have a SECOND fetch here
// (`workspaceProject.workspaceDashboardDefaults` — a separate
// workspace_dashboard_defaults row) that unconditionally merged into
// root.workspaceSettings once its network call resolved. Because it ran
// inside this same fire-and-forget fan-out, it settled AFTER the synchronous
// `applyWorkspaceConfig(root)` call that already precedes this fan-out at both
// call sites below (which resolves home_default_panels from the canonical
// Mongo `Workspace.settings` bag) — so the row was always the LAST writer and
// always won, regardless of which admin surface (/admin/module Panels tab vs
// /admin/dashboard-defaults) was actually current. Both surfaces now write
// the same `Workspace.settings.home_default_panels` key (merge-safe, via
// sdk.updateWorkspaceSettings — see pages/admin/dashboard-defaults.js +
// functions/moduleConsole.js mcSavePersonalization), so the row fetch — and
// the re-apply that existed only to reconcile it — are retired. See
// tickets/workspace.md "home_default_panels dual-store fight" and
// server/scripts/seed-greenig-workspace.mjs Part B (the row was already being
// deleted server-side for a related federation-token cross-workspace leak).
// Exported (not just module-private) so auth.js's `_ensureWorkspaceModule` can
// re-run the SAME fetch once the active workspace resolves on a tick AFTER
// loadPrivateData's own `_prefsChain` already ran — see the call site there for
// why that second fetch is required, not optional.
export const _dashboardPrefsFetches = (
  root,
  sdk,
  { tag = '', workspaceId = null, write = null, primed = null, onError = null } = {}
) => {
  // A comm host has no home dashboard: no layout row, no widget definitions,
  // and nothing for the reveal to wait on (all three callers await this list).
  if (shellIsCommHost()) return []
  // Boot, post-auth reconciliation and manual retry can all read the same
  // row concurrently. Only the latest request may publish its layout.
  const request = {}
  _dashboardPrefsReads.set(root, request)
  // Pin the read to the workspace the TAB IS SHOWING — record → per-tab
  // pointer → claim, the same precedence as pages/main.js `_wsScope` (the
  // WRITE path), so the read and the write can never target two different
  // rows. The claim used to come FIRST here, but it is SESSION-global and
  // not re-minted by a URL-pinned boot: a /w/<slug> load while another
  // org's workspace was still active in the claim pulled THAT workspace's
  // row into this dashboard (cross-tenant hidden/layout leak, observed
  // live on /w/system 2026-07-27). Switch callers pass the target
  // workspace explicitly — root.workspace can still be the previous
  // record when the switch's getWorkspace refetch failed.
  const wsId =
    workspaceId ||
    root?.workspace?.id ||
    root?.workspace?._id ||
    shellGetActiveWorkspacePointer() ||
    root?.jwtClaims?.active_workspace_id ||
    null
  // A dashboard row belongs to the workspace that issued its request, not to
  // whichever root happens to be live when the network response returns. The
  // switch path supplies its own epoch-guarded writer below; the ordinary boot
  // path needs the same ownership boundary or a late System response can
  // overwrite the newly-resolved workspace's layout immediately before reveal.
  // `workspace` wins here for the same "tab is showing" precedence used to
  // choose `wsId` above. If the shell no longer has a concrete workspace, the
  // response is deliberately dropped rather than guessed into an unscoped
  // dashboard.
  const writeForCapturedWorkspace = (patch) => {
    if (!wsId) {
      root.update(patch)
      return true
    }
    const liveWorkspaceId = root?.workspace?.id || root?.workspace?._id || null
    if (!liveWorkspaceId || String(liveWorkspaceId) !== String(wsId)) return false
    root.update(patch)
    return true
  }
  // `write` lets the workspace-switch path route through its epoch-guarded
  // `put` so a superseded switch's late row never repaints the newer one.
  const put = (patch) => {
    if (_dashboardPrefsReads.get(root) !== request) return false
    return (write || writeForCapturedWorkspace)(patch)
  }
  // AI-CREATED home widgets (tickets/opus.md "Per-workspace AI-CREATED
  // widgets") ride this SAME chain, deliberately: they are per-(user,
  // workspace) content exactly like the dashboard prefs, so they must re-scope
  // on every workspace switch through the same epoch-guarded `put` and the
  // same "tab is showing" workspace pin. Adding a separate fetch elsewhere
  // would be a second place to get the switch isolation wrong.
  //
  // Each row carries its own `workspaceId` and the CALLER's own
  // `viewerCapability` (resolved server-side for whoever is asking, never the
  // creator) — `functions/aiWidgets.js aiWidgetsFor` filters to the live
  // workspace on read, so even a superseded response cannot paint the next
  // tenant's board. A failure leaves the plane empty rather than stale: the
  // home board simply shows no AI widgets, which is the correct degrade.
  // Kicked BEFORE the prefs prime check below so the primed path fetches it
  // too — /core/boot has no widget-def section to prime from.
  //
  // SKIPPED ENTIRELY without a resolved workspace. Unlike the prefs read below
  // — whose unscoped form still returns the caller's own row — an unscoped
  // widget-def list resolves against the TOKEN CLAIM's workspace, which during
  // a URL-pinned boot is routinely a DIFFERENT workspace than the tab is
  // showing. That call returns that workspace's widgets (usually none) and,
  // because `writeForCapturedWorkspace` cannot compare an id it never
  // captured, its `put` writes UNCONDITIONALLY — so a boot-time claim-scoped
  // empty answer landed on top of the URL workspace's real plane and the board
  // showed no AI widgets at all (observed live on /w/natali/natali-backup,
  // 2026-08-13). Every call site re-runs this chain once the workspace
  // resolves (the workspace-change fan-out and auth.js's `_ensureWorkspaceModule`
  // re-fetch), so skipping here costs nothing and removes the whole race.
  const aiWidgets = wsId
    ? sdk
        .execute('workspaceProject.widgetDefs', 'list', { workspaceId: wsId })
        .then((rows) => {
          put({ workspaceAiWidgets: Array.isArray(rows) ? rows : [] })
        })
        .catch((e) => {
          console.warn(`[workspace-project] ${tag}ai widget defs fetch failed`, e)
          if (_dashboardPrefsReads.get(root) === request) onError?.('widgets')
        })
    : Promise.resolve()

  // Composite-boot prime: /core/boot's `prefs` section replays the EXACT
  // SAME handler (PrefsController.getHomeDashboard, via runSection) this GET
  // itself hits — server/src/domains/boot/controllers/BootController.js and
  // sdk/src/services/WorkspaceProjectService.js both terminate at GET
  // /core/prefs/home-dashboard. Plane equivalence (tickets/workspace.md
  // "home-dashboard prefs are NOT primed…") is proven, not assumed: a live
  // same-session comparison (2026-08-08) of `/core/boot`'s `data.prefs`
  // against a direct `/core/prefs/home-dashboard` call, same user+workspace,
  // both with and without an explicit `workspaceId`, returned BYTE-IDENTICAL
  // JSON both times — there is only one store.
  // Still only trusted when NON-null AND scoped to the SAME workspace this
  // call resolves to (a fast in-app switch can race a still-in-flight boot).
  // A null section is deliberately NOT treated as "confirmed empty": boot's
  // `Promise.allSettled` collapses a genuinely-empty row and a
  // rejected/errored section to the same `data.prefs: null` — trusting that
  // as empty would repaint the user's real saved layout with defaults on a
  // masked error, the exact regression class this file exists to prevent.
  // Falls through to the network read below in every other case.
  if (
    primed &&
    primed.prefs &&
    primed.workspaceId &&
    wsId &&
    String(primed.workspaceId) === String(wsId)
  ) {
    put({ _dashPrefsReadOk: true, userDashboardPrefs: primed.prefs })
    return [aiWidgets]
  }

  // One bounded retry — a single transient failure (API restarting under
  // nodemon, cold pool) otherwise leaves the whole boot on the stale local
  // cache, which is the top source of "widgets differ after reload".
  const read = (attempt = 0) =>
    sdk
      .execute('workspaceProject.homeDashboardPrefs', 'get', wsId ? { workspaceId: wsId } : {})
      .catch((e) => {
        if (attempt < 1) return new Promise((r) => setTimeout(r, 400)).then(() => read(attempt + 1))
        throw e
      })

  return [
    read()
      .then((data) => {
        // `_dashPrefsReadOk` flips on ANY successful read — including an
        // empty one (user genuinely has no row yet). The grid boot gates its
        // default-seed persist on it, so a FAILED read can never end with
        // defaults written over the user's real row.
        put({ _dashPrefsReadOk: true, ...(data ? { userDashboardPrefs: data } : {}) })
      })
      .catch((e) => {
        console.warn(`[workspace-project] ${tag}home_dashboard_prefs fetch failed`, e)
        if (_dashboardPrefsReads.get(root) === request) onError?.('dashboard')
      }),
    aiWidgets
  ]
}

// ── §6 — LATE ARRIVAL MUST RE-RENDER ─────────────────────────────────────────
// (tickets/spec-workspace-record-shape.md §6.)
//
// §4c guarantees the full workspace record is always FETCHED once any id is
// resolved, on any route. It does not make anything repaint when it lands, and
// the navbar is the case that proves why that gap matters:
//
//   root.workspace.settings.navbar          (source of truth, async)
//     → resolveWorkspaceConfig              (workspaceConfigs.js)
//       → applyWorkspaceConfig              (publishes the DERIVED bag)
//         → root.workspaceSettings.navbar   (what HeaderTabs actually reads)
//
// HeaderTabs never reads the record. It reads the derived bag. So a record
// arriving after applyWorkspaceConfig last ran changes NOTHING on screen — the
// bare DEFAULT_NAVBAR that the early lean-boot pass published stays painted,
// indefinitely, over a workspace that has a configured navbar. That is exactly
// the reported "/add-app shows the default navbar" symptom, and it survives
// every part of §4 on its own.
//
// The activation chain is re-run on the upgrade edge. All three steps, not just
// applyWorkspaceConfig: on a lean boot the chain's first pass ran with NO
// workspace id at all (loadPrivateData's `_wsReady` only waits when a URL slug
// forces the workspace, which is precisely why the non-/w/* routes lose this
// race), so `root.workspaceProject` and `root.workspaceApps` are unresolved too
// — and they are precedence 1 and the navbar-append source in
// resolveWorkspaceConfig. Both loaders carry their own in-flight dedup keyed by
// a signature that INCLUDES the settings bag (`_loaderFlightKey`), so the
// pre-arrival pass and this post-arrival pass key differently and this one
// genuinely re-runs rather than joining the stale flight.
//
// applyWorkspaceConfig then corrects itself without further help: its
// `savedNavbarIsBareDefault` guard already exists so a previously-published
// bare default loses to a preset that carries a real navbar. It was only ever
// missing this trigger.
let _convergeRegistered = false
let _convergeSdk = null
let _convergeSeq = 0

export const registerWorkspaceConfigConverge = (sdk) => {
  if (sdk) _convergeSdk = sdk
  if (_convergeRegistered) return
  _convergeRegistered = true
  onActiveWorkspaceFull((root, ws) => {
    const wsId = String((ws && (ws.id || ws._id)) || '')
    if (!root || !wsId || !_convergeSdk) return
    // auth.js has already started the module/apps/config + prefs chain for
    // this exact late-claim workspace and owns the loader reveal. Starting a
    // second converge pass here races the same reactive config publication and
    // is the source of the extra post-sign-in chrome/layout shuffle.
    if (isPostAuthWorkspaceReveal(root, wsId)) return
    // Last-upgrade-wins: a workspace switch landing while this chain is in
    // flight must not have the older workspace's config applied over it.
    const seq = ++_convergeSeq
    const stale = () =>
      seq !== _convergeSeq ||
      String((root.workspace && (root.workspace.id || root.workspace._id)) || '') !== wsId
    Promise.resolve()
      .then(async () => {
        // Boot entrant (see auth.js's post-sign-in chain): reuse a settled pass.
        await loadWorkspaceProject(root, _convergeSdk, { boot: true })
        if (stale()) return
        await loadWorkspaceApps(root, _convergeSdk, { boot: true })
        if (stale()) return
        applyWorkspaceConfig(root)
      })
      .catch((e) =>
        console.warn('[workspace-project] full-record converge: applyWorkspaceConfig failed', e)
      )
  })
}

export const loadPrivateData = async (root, sdk, _opts = {}) => {
  if (!root || !sdk) return
  // Detached nodes are normal during mount. Only explicit disposal markers
  // reject a root; keep its element identity so a queued pass cannot revive
  // a removed element or an older binding of the same state object.
  const _loadElement = root.__element || null
  if (_loadElement?.__destroyed || _loadElement?.__ref === null) return
  // `getWorkspaceCached` (functions/getWorkspaceCached.js) closes over its own
  // module-scope `_inflight`/`_cache` dedup state — a raw cross-file `import`
  // reference to it is a free identifier once this function is serialized and
  // revived standalone (frank can't lift a closure with private module state
  // into globalScope; it leaves the reference unresolved instead — confirmed:
  // `functions.loadPrivateData`/`functions.onWorkspaceChange` both threw
  // ReferenceError at a frank-served runtime, silently breaking the boot data
  // load AND every workspace-switch refetch). `root.call` reaches the SAME
  // live singleton via `context.functions` (populated at runtime, not
  // re-evaluated), so the cache/dedup state stays intact.
  // HARDENED (P1 lead, 2026-08-15): `root.call` is ABSENT on some state
  // objects mid-switch — bellf-qa captured the TypeError thrown from THIS
  // wrapper synchronously inside onCreate (rootBus replays orgSwitched to a
  // late subscriber during createDomqlElement), and an uncaught lifecycle
  // throw aborts element creation mid-tree, leaving later handlers unbound.
  // Fallback: the direct import reaches the SAME in-bundle singleton (cache
  // intact); under frank revival the import vanishes but `typeof` of the
  // free identifier is safely 'undefined' and we degrade to a null read
  // instead of a throw.
  const getWorkspaceCached = (...args) =>
    typeof root.call === 'function'
      ? root.call('getWorkspaceCached', ...args)
      : typeof _gwcDirect === 'function'
        ? _gwcDirect(...args)
        : Promise.resolve(null)
  // Register BEFORE the early-return guards below: a duplicate/queued pass
  // still has to leave the late-arrival reaction armed, and the record can
  // land during a pass this one dropped.
  registerWorkspaceConfigConverge(sdk)
  const _scopeNow = _fetchScope(root, sdk)
  if (_shellLoadInFlight) {
    if (
      _shellLoadInFlight.root === root &&
      _shellLoadInFlight.element === _loadElement &&
      (_scopeNow === _loadScopeAtStart || _shellLoadInFlight.generation !== scopeGen())
    ) {
      // A switch that already superseded this root's boot owns its new scope
      // and reveal. A late auth callback must not take the switch's epoch.
      return
    }
    const sameQueuedRoot =
      _loadRerunQueued?.root === root && _loadRerunQueued.element === _loadElement
    if (
      sameQueuedRoot &&
      (_dataLoadEpochByRoot.get(root) !== _loadRerunQueued.epoch ||
        (_loadRerunQueued.generation === scopeGen() && _loadRerunQueued.scope === _scopeNow))
    ) {
      // A newer reveal on THIS root owns its hydration. Otherwise only a
      // current-generation duplicate can share the queued request: a switch
      // on the old root must not discard this replacement's later request.
      return
    }
    const queued = {
      root,
      sdk,
      // Auth can repeat a request without its original composite payload.
      // Retain that payload when renewing this root's same-scope handoff.
      opts:
        sameQueuedRoot && _loadRerunQueued.scope === _scopeNow
          ? { ..._loadRerunQueued.opts, ..._opts }
          : _opts,
      element: _loadElement,
      scope: _scopeNow,
      generation: scopeGen(),
      epoch: null
    }
    // Publish before update(): a synchronous subscriber may queue an even
    // newer root. Its record and epoch must win over this caller's return.
    _loadRerunQueued = queued
    queued.epoch = beginDataLoad(root)
    return
  }
  // Also cancels a previously scheduled handoff when a fresh call arrives in
  // the gap after the old pass finishes but before its handoff task runs.
  _loadRerunQueued = null
  // This pass's own latch record: a session end (endWorkspaceSession) can
  // release it while the pass runs, and the next session's pass then holds
  // its own.
  const _loadLatch = { root, element: _loadElement, generation: scopeGen() }
  _shellLoadInFlight = _loadLatch
  _loadScopeAtStart = _scopeNow
  const _loadWsGen = _loadLatch.generation
  const _bootGeneration = ++_bootLoadGeneration
  const _bootUser = root.currentUserId
  let _bootWorkspace = null
  let _bootOrg = null
  const _bootCurrent = () =>
    _bootGeneration === _bootLoadGeneration &&
    !scopeStale(_loadWsGen) &&
    root.signedIn !== false &&
    root.currentUserId === _bootUser &&
    (_bootWorkspace === null ||
      String(root.workspace?.id || root.workspace?._id || '') === _bootWorkspace) &&
    (_bootOrg === null || String(root.org?.id || root.org?._id || '') === _bootOrg)
  // The stamp of the core entry this pass wrote last. Every settle below acts
  // on that entry only, never on a newer owner's.
  let _coreStamp = 0
  // A pass that stops being current while NOTHING replaced it must not leave
  // its entry behind: nobody else will ever finish it. An identity re-stamp
  // mid-boot is such a shape (activationNoticeSettles.test.js): auth's tick
  // moves `currentUserId` and its `lastUserId`, but starts no second pass while
  // this one is in flight, so "still loading" (or a retry's "Retrying…") stayed
  // up for good. The entry turns into the retryable failure, so Retry runs a
  // pass that is current. A successor owns the status (a newer pass writes core
  // as it starts; a switch wipes it), so a superseded pass leaves it alone.
  const _releaseCore = () => {
    if (_bootGeneration !== _bootLoadGeneration || scopeStale(_loadWsGen)) return
    settleWorkspaceActivation(root, 'core', _coreStamp, { state: 'error' })
  }
  const _bootErrors = []
  // Roster + team adoption also require that the SDK has not moved to ANOTHER
  // workspace since the roster was read (captured in the people chain once
  // the workspace resolves). The SDK's own mirror of the viewed workspace
  // (auth's updateContext) is not a move, and neither is no change.
  let _rosterCaptured = false
  let _rosterWs = null
  let _rosterSdkWs = null
  const _sdkWs = () =>
    sdk?._context?.activeWorkspaceId ? String(sdk._context.activeWorkspaceId) : null
  const _rosterCurrent = () => {
    if (!_bootCurrent()) return false
    if (!_rosterCaptured) return true
    const now = _sdkWs()
    return !now || now === _rosterWs || now === _rosterSdkWs
  }
  const _retryReconciliation = _postAuthWorkspaceRevealPending.get(root)
  // SHELL-USER-KEY-1: warm-start boots can carry currentUser* from the
  // cached snapshot while the full root.user record never landed (getMe
  // failed ambiguously and nothing retried the stamp). One-shot,
  // non-blocking heal — full rationale + tests in healUserRecord.js.
  healUserRecord(root, sdk)
  // Keep the boot loader (MainLoader, gated by `_dataLoading` in _isLoaderActive)
  // up until the awaited CORE workspace data lands — people + the workspace
  // project (skin/navbar/panels/seed records) + the resolved config. The inline
  // #boot-spiral only covers up to shell MOUNT; without this the shell renders
  // empty-then-fills while data is still loading. Cleared in `finally` (covers
  // the no-active-workspace early return too, so the loader never sticks on
  // /login) — via the ownership epoch, so a boot pass finishing mid-org-switch
  // can no longer drop the loader the switch raised. The secondary fan-out
  // below stays non-blocking.
  const _dlEpoch = beginDataLoad(root, {
    __fetchScope: _scopeNow,
    ...(_opts.background ? { _dataLoading: false } : {})
  })
  _coreStamp = updateWorkspaceActivation(root, 'core', {
    state: 'loading',
    visible: !!_opts.background
  })
  _mark('wsboot:priv:start')
  // Hoisted so `finally` can hold a queued root handoff until they settle.
  let _projectChain = null
  // Boot fix step 2: the part of the project chain the REVEAL waits for —
  // the takeover module plus, on a deep link, the one app whose page the URL
  // names (see whenAppRouteReady). The rest of the apps keep setting up
  // after the page shows; `_projectChain` still covers all of it for the
  // activation status (core 'ready' only after every app settled).
  let _projectReveal = null
  let _prefsChain = null
  let _peopleChain = null
  try {
    // A legacy federated JWT remnant is OPTIONAL noise — when present, the
    // wrapper fast-paths via the `workspace_id` claim; when absent, the
    // wrapper verifies the SDK Mongo token with the symbols-server
    // secret and resolves `workspace_id` from `User.activeOrganization`
    // via the Express-injected Mongo userResolver. Either path works,
    // so we don't gate the data load on federation anymore — every
    // signed-in user gets full data.
    //
    // We still wait briefly for the workspace-project JWT so the
    // fast-path is preferred when federation is in flight (saves the
    // wrapper a Mongo lookup per request). 500ms ceiling — either path
    // works fine; no need to block the full data load for 2s on cold start.
    // Run people fetch in parallel with the JWT wait so the two don't
    // stack: people data is ready as soon as the longer of the two finishes.
    //
    // The people fetch doubles as a canary: when the user has no active
    // workspace yet (cold /login, mid-org-switch), the server replies
    // `{ error: 'no active workspace for user' }` (auth.js:302). Every
    // subsequent fetch in the fan-out below would fail with the same
    // error and each .catch would surface as its own /logs bug row — we
    // observed 10+ identical warns per /login page load
    // (WORKSPACE-LOGS-PRIVATEDATA-PREAUTH-1). Detect that case and
    // short-circuit; subscribeAuthSession will re-fire loadPrivateData
    // the moment a valid session + active_workspace_id lands.
    let _peopleErr = null
    // Consume the entry-primed roster (fired in index.js at page entry, in
    // parallel with the entire auth+mount boot) so the people list no longer
    // waits on the boot waterfall. The roster is authed by the SDK Mongo token +
    // org-scoped server-side. Falls back to a fresh fetch when
    // the prime is absent/failed (zero-regression worst case).
    // Multi-tab drift guard: the prime is trustworthy ONLY when it was itself
    // scoped to the tab's viewed org — peoplePrime sends the /w/<slug> org
    // segment as `orgSlug` (membership-verified server-side) and records it.
    // A prime scoped to a DIFFERENT slug (or an unscoped prime on a /w/ URL)
    // planted the token-org's roster and must be skipped in favor of the
    // explicitly scoped fetch below. NOTE: comparing against the SDK's
    // client-side activeWorkspaceId mirror is NOT proof — auth.js mirrors the
    // resolved workspace into it while the prime's response was scoped by the
    // SERVER-side claim (learned live, 2026-07-22).
    const _urlSlug =
      (/^\/w\/([^/]+)/.exec(globalThis.location?.pathname || '') || [])[1]?.toLowerCase() || null
    const _primeSafe = !_urlSlug || primedOrgSlug() === _urlSlug
    const _primedPeople = _primeSafe ? consumePrimedPeople() : null
    // Composite-boot members: /core/boot's `members` section is byte-
    // equivalent to the /core/users/members rows this path fetches, and
    // bootShell initially scope-checks this payload, but a queued load can
    // reuse its options after the workspace changes. Re-check the workspace
    // after _wsReady below before adopting any composite rows.
    // An empty array is an authoritative empty roster, not a missing section.
    const _bootMembers = Array.isArray(_opts?.bootPayload?.members)
      ? _opts.bootPayload.members
      : null
    // Composite-boot prefs — plane equivalence proven (see
    // _dashboardPrefsFetches's own comment for the live-verified proof);
    // handed to _dashboardPrefsFetches below as `primed`, which re-checks
    // the workspace scope itself before trusting it.
    const _bootPrefsPrime = _opts?.bootPayload?.prefs
      ? { workspaceId: _opts.bootPayload.workspaceId, prefs: _opts.bootPayload.prefs }
      : null
    // Keep the initial loader up until the known workspace lands on BOTH
    // URL-scoped and ordinary post-sign-in boots. The helper degrades after a
    // bounded wait when there is no organization/workspace to resolve.
    const _wsReady = awaitInitialWorkspace(root, { urlScoped: !!_urlSlug }).then(() => {
      if (!_bootCurrent()) return
      _bootWorkspace = String(root.workspace?.id || root.workspace?._id || '')
      _bootOrg = String(root.org?.id || root.org?._id || '')
      _coreStamp = updateWorkspaceActivation(root, 'core', {
        state: 'loading',
        visible: !!_opts.background
      })
    })
    // The persona oracle (GET /persona) rides the data wave instead of waiting
    // for the stage-CHROME PersonaPill mount (dev 1061f366e: 3253 ms, after the
    // module data). It asks once the boot holds its workspace, through the
    // pill's own gate (functions/sessionStreams.js personaOracleReady): the
    // worker authenticates /persona against the session's active workspace,
    // so a signed-in boot with none got a 401 "no active workspace for user"
    // (staging 2026-10-31: a new member's first boot, on the invite page before
    // the accept writes the org). `_wsReady` settles at once when the boot
    // already holds its workspace, so the ask still starts with the data wave.
    // Fire-and-forget; fails safe; the pill's mount call is then a no-op
    // (shared/functions/persona.js).
    if (!_opts.background && _loadElement && typeof _loadElement.call === 'function') {
      _wsReady
        .then(() => {
          if (!_bootCurrent() || !_loadElement.call('personaOracleReady', root)) return
          _loadElement.call('refreshPersonaSession', { boot: true })
        })
        .catch(() => {})
    }
    // WORKSPACE-BOOT-PERF: start the project/apps/config chain NOW, in
    // parallel with the people fetch — it never needed the roster (the
    // onWorkspaceChange switch path has run them concurrently for a while;
    // boot lagged behind and serialized people → project → apps, which is
    // exactly the late-navbar window HeaderTabs.js documents). Awaited
    // below before the loader drops, so the contract (loader covers
    // people + project + config) is unchanged — only the wall-clock is
    // now max(people, project+apps) instead of the sum.
    let _releaseProjectReveal = () => {}
    _projectReveal = new Promise((resolve) => {
      _releaseProjectReveal = resolve
    })
    _projectChain = _wsReady
      .then(async () => {
        if (!_bootCurrent()) return
        _mark('wsboot:module:ws-ready')
        // A comm host loads no workspace module and no apps: their routes open
        // on the workspace host (docs/COMM_HOST.md), and a communication boot
        // must not wait on them (measured 0.95 s on dev, 2026-10-23).
        if (shellIsCommHost()) {
          _releaseProjectReveal()
          try {
            applyWorkspaceConfig(root)
          } catch (e) {
            console.warn('[workspace-project] applyWorkspaceConfig (comm) failed', e)
          }
          return
        }
        // Boot entrant: an earlier entrant's settled pass is reused, not re-run.
        await loadWorkspaceProject(root, sdk, { boot: true })
        if (!_bootCurrent()) return
        _mark('wsboot:module:project-done')
        const _appsRun = loadWorkspaceApps(root, sdk, { boot: true })
        // The reveal gate: the takeover is in, and the page the URL names is
        // routable (the home needs no app). Config applies here for the
        // takeover and again below once the apps have published.
        whenAppRouteReady(root).then(() => {
          if (!_bootCurrent()) return _releaseProjectReveal()
          _mark('wsboot:module:route-ready')
          try {
            applyWorkspaceConfig(root)
          } catch (e) {
            console.warn('[workspace-project] applyWorkspaceConfig (reveal) failed', e)
          }
          _releaseProjectReveal()
        })
        await _appsRun
        if (!_bootCurrent()) return
        _mark('wsboot:module:apps-done')
        applyWorkspaceConfig(root)
        _measure('wsboot:ws-resolve', 'wsboot:priv:start', 'wsboot:module:ws-ready')
        _measure('wsboot:module-project', 'wsboot:module:ws-ready', 'wsboot:module:project-done')
        _measure('wsboot:module-apps', 'wsboot:module:project-done', 'wsboot:module:apps-done')
      })
      .catch((e) => {
        console.warn('[workspace-project] applyWorkspaceConfig (boot) failed', e)
        _bootErrors.push('module')
      })
      .finally(() => _releaseProjectReveal())
    // First-paint prefs — the home grid layout + hidden-widget set
    // (userDashboardPrefs) decide the FIRST visible frame. They used to
    // ride the post-boot fan-out below, which
    // fires at the exact boot-ready falling edge — measured live, the row
    // landed ~340ms AFTER the loader dropped, so the home grid assembled on
    // cache/spec defaults and visibly re-shuffled on every boot (2026-07-27
    // per Nika "widgets bounce after loading … should get initial data
    // during initial load and render accordingly"). Start them the moment
    // the workspace resolves and await them WITH the project chain so the
    // layout and saved widget definitions are ready together.
    _prefsChain = _wsReady
      .then(() => {
        if (!_bootCurrent()) return
        return Promise.all(
          _dashboardPrefsFetches(root, sdk, {
            primed: _bootPrefsPrime,
            write: (patch) => {
              if (_bootCurrent()) root.update(patch)
            },
            onError: (phase) => _bootErrors.push(phase)
          })
        )
      })
      .catch((error) => {
        console.warn('[workspace-project] dashboard preferences failed', error)
        _bootErrors.push('dashboard')
      })

    // Account preferences refresh behind the ready workspace. The bounded
    // boot theme preflight/cache already owns first paint; waiting for this
    // second read used to keep the full-screen loader over otherwise-ready
    // pages for the duration of a slow /prefs/user request. The saved dashboard
    // layout above remains a reveal prerequisite so widgets do not reshuffle.
    const _prefsUserAtStart = root.currentUserId
    const _prefsThemeAtStart = root.themeMode
    const _prefsStoredThemeAtStart = shellReadStoredThemeMode()
    const _prefsThemeUnchanged = () =>
      root.themeMode === _prefsThemeAtStart && shellReadStoredThemeMode() === _prefsStoredThemeAtStart
    // The assistant dock rides the same row with the same discipline
    // (ASSISTANT_BOARD_MODE_SPEC §1, functions/assistantDock.js): a dock the
    // user moves while the read is pending is newer than its result.
    const _prefsDockAtStart = root.assistantDock
    const _prefsStoredDockAtStart = readStoredAssistantDock()
    const _prefsDockUnchanged = () =>
      root.assistantDock === _prefsDockAtStart &&
      readStoredAssistantDock() === _prefsStoredDockAtStart
    // The assistant's MODE rides the same row (functions/assistantMode.js).
    const _prefsModeAtStart = root.assistantMode
    const _prefsStoredModeAtStart = readStoredAssistantMode()
    const _prefsModeUnchanged = () =>
      root.assistantMode === _prefsModeAtStart &&
      readStoredAssistantMode() === _prefsStoredModeAtStart
    // …and Agent mode (functions/agentMode.js).
    const _prefsAgentAtStart = root.agentMode
    const _prefsStoredAgentAtStart = readStoredAgentMode()
    const _prefsAgentUnchanged = () =>
      root.agentMode === _prefsAgentAtStart && readStoredAgentMode() === _prefsStoredAgentAtStart
    // …and the sidebar's width (functions/assistantWidth.js).
    const _prefsWidthAtStart = root.assistantWidth
    const _prefsStoredWidthAtStart = readStoredAssistantWidth()
    const _prefsWidthUnchanged = () =>
      root.assistantWidth === _prefsWidthAtStart &&
      readStoredAssistantWidth() === _prefsStoredWidthAtStart
    void _wsReady
      .then(async () => {
        // Capture the resolved scope at request time: the initial auth tick
        // can precede the authoritative workspace record.
        const prefsScope = _fetchScope(root, sdk)
        const prefsWorkspace = String(root.workspace?.id || root.workspace?._id || '')
        const _accountPrefsCurrent = () =>
          _bootGeneration === _bootLoadGeneration &&
          !scopeStale(_loadWsGen) &&
          _fetchScope(root, sdk) === prefsScope &&
          String(root.workspace?.id || root.workspace?._id || '') === prefsWorkspace &&
          root.currentUserId === _prefsUserAtStart &&
          root.signedIn !== false
        if (!_accountPrefsCurrent()) return
        const data = await sdk.execute('workspaceProject.userPreferences', 'get')
        if (!_accountPrefsCurrent()) return
        // A choice made while the read was pending is newer than its result.
        // Preserve it in BOTH the runtime mode and the mirrored account row.
        const themeUnchanged = _prefsThemeUnchanged()
        // A NEW account has no row yet — the server answers null. The read
        // still FINISHED: the row is loaded and empty (`{}`), the same value
        // every writer composes on (functions/homeLook.js). `null` keeps
        // meaning "not read yet", which readers of the record wait on — the
        // new-account welcome among them (functions/onboarding.js).
        const row = data && typeof data === 'object' ? data : {}
        root.update({
          userPrefs: themeUnchanged ? row : { ...row, themeMode: root.themeMode }
        })
        if (_prefsDockUnchanged()) {
          const dockDecision = reconcileAssistantDockFromPrefs(
            root.assistantDock,
            data,
            readStoredAssistantDock()
          )
          if (dockDecision?.apply) {
            root.update({ assistantDock: dockDecision.apply, _assistantDockPromotedFrom: null })
            try {
              localStorage.setItem('symbols_assistant_dock', dockDecision.apply)
              // the record holds a person's choice: marked on this device too,
              // so the next boot paints it at once (functions/assistantDock.js)
              localStorage.setItem('symbols_assistant_dock_explicit', '1')
            } catch (_) {
              /* storage unavailable — the state still follows the record */
            }
          } else if (dockDecision?.migrate) {
            // Through the one writer, record only (the state and this device
            // already hold the choice): single-flight on the shared prefs
            // queue, the payload composed from the live row at send time.
            setAssistantDock(root, dockDecision.migrate, sdk, { recordOnly: true })
          }
        }
        if (_prefsModeUnchanged()) {
          const modeDecision = reconcileAssistantModeFromPrefs(
            root.assistantMode,
            data,
            readStoredAssistantMode()
          )
          if (modeDecision?.apply) {
            // A comm host SHOWS a comm mode for the account's choice; the
            // stored copy below keeps the account's own value.
            root.update({
              assistantMode: _wsShared.functions.commAssistantMode(modeDecision.apply)
            })
            try {
              localStorage.setItem('symbols_assistant_mode', modeDecision.apply)
            } catch (_) {
              /* storage unavailable — the state still follows the record */
            }
          } else if (modeDecision?.migrate) {
            // Through the one writer: single-flight on the shared prefs
            // queue, the payload composed from the live row at send time.
            setAssistantMode(root, modeDecision.migrate, sdk)
          }
        }
        if (_prefsAgentUnchanged()) {
          const agentDecision = reconcileAgentModeFromPrefs(
            root.agentMode,
            data,
            readStoredAgentMode()
          )
          if (agentDecision && 'apply' in agentDecision) {
            root.update({ agentMode: agentDecision.apply })
            try {
              localStorage.setItem('symbols_agent_mode', agentDecision.apply ? '1' : '0')
            } catch (_) {
              /* storage unavailable — the state still follows the record */
            }
          } else if (agentDecision && 'migrate' in agentDecision) {
            // Through the one writer: single-flight on the shared prefs queue.
            setAgentMode(root, agentDecision.migrate, sdk)
          }
        }
        if (_prefsWidthUnchanged()) {
          const widthDecision = reconcileAssistantWidthFromPrefs(
            root.assistantWidth,
            data,
            readStoredAssistantWidth()
          )
          if (widthDecision?.apply) {
            root.update({ assistantWidth: widthDecision.apply })
            try {
              localStorage.setItem('symbols_assistant_width', String(widthDecision.apply))
            } catch (_) {
              /* storage unavailable — the state still follows the record */
            }
          } else if (widthDecision?.migrate) {
            setAssistantWidth(root, widthDecision.migrate, sdk)
          }
        }
        if (!themeUnchanged) return
        // The TENANT default the boot seeded: the workspace record's own
        // (`settings.theme` — GreenIG's dark) before the brand's, the same
        // order bootResolve seeds them. Without it, an account row that only
        // carries 'auto' (= no preference) undid the seeded default here.
        const decision = reconcileThemeModeFromPrefs(
          root.themeMode,
          data,
          shellReadStoredThemeMode(),
          shellWorkspaceThemeModeOf(root.workspace) || shellReadTenantBootThemeMode()
        )
        if (decision?.apply) {
          const { changeGlobalTheme } = await import('smbls')
          if (!_accountPrefsCurrent() || !_prefsThemeUnchanged()) return
          root.update({ themeMode: decision.apply })
          shellWriteStoredThemeMode(decision.apply)
          changeGlobalTheme(decision.apply)
        } else if (decision?.migrate) {
          persistThemeModeToUser(root, sdk, decision.migrate)
        }
      })
      .catch((e) => console.warn('[workspace-project] user_preferences fetch failed', e))
    _peopleChain = (async () => {
      await _wsReady
      if (!_bootCurrent()) return
      // Read identity AFTER initial resolution so a cold boot's pending
      // workspace is not mistaken for a switch. _bootCurrent() keeps the
      // load-start generation: onWorkspaceChange owns the replacement roster
      // if a switch ran meanwhile, including A → B → A where the final ids
      // match this older request's scope. The SDK scope joins it: a request
      // the SDK has moved away from can no longer name this roster.
      const _viewedWs = String(root.workspace?.id || root.workspace?._id || '') || null
      _rosterWs = _viewedWs
      _rosterSdkWs = _sdkWs()
      _rosterCaptured = true
      let peopleData = null
      if (_bootMembers) {
        if (_viewedWs && String(_opts.bootPayload.workspaceId || '') === _viewedWs) {
          peopleData = _bootMembers
        }
        // A mismatched composite payload must use the explicitly scoped read
        // below, not a potentially older entry prime retained by a legacy SDK.
      } else if (_primedPeople) {
        peopleData = await _primedPeople
      }
      if (!peopleData && _rosterCurrent()) {
        peopleData = await shellListOrgMembers(sdk, _viewedWs).catch((e) => {
          _peopleErr = e
          return null
        })
      }
      // The scoped request can finish after a newer workspace load has already
      // put its roster. Neither old rows nor old failures may replace it.
      if (!_rosterCurrent()) return
      if (_peopleErr) {
        const msg = _peopleErr?.message || String(_peopleErr)
        _bootErrors.push('people')
        if (/no active workspace for user/i.test(msg)) return
        console.warn('[workspace-project] people fetch failed', _peopleErr)
        // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — record it: `peopleError` is what
        // lets the Home People count render '!' instead of the same 0 an empty
        // workspace shows.
        root.update({ peopleError: msg })
      }

      if (peopleData) {
        const all = peopleData.map(_person)
        // #2309 — `users.members` is already org-scoped server-side, so the full
        // list IS the active-org roster. filterToActiveOrg here raced ahead of
        // orgMembersByOrgId loading and hit its self-only fallback, leaving the
        // assignee picker with just the current user. Use the full list directly.
        root.update({ peopleAll: all, people: all, peopleLoaded: true, peopleError: null })
      }
      _mark('wsboot:priv:people-done')
    })()
    const _coreWork = Promise.all([_projectChain, _prefsChain, _peopleChain])
      .then(() => {
        if (!_bootCurrent()) return _releaseCore()
        _mark('wsboot:priv:project-done')
        _mark('wsboot:priv:prefs-done')
        updateWorkspaceActivation(root, 'core', {
          state: _bootErrors.length ? 'error' : 'ready',
          failed: [...new Set(_bootErrors)]
        })
        // A successful explicit refresh replaces the stalled post-auth read
        // too; its obsolete promise must not keep a permanent slow notice.
        if (
          _opts.background &&
          !_bootErrors.length &&
          _retryReconciliation?.workspaceId === _bootWorkspace
        ) {
          endPostAuthWorkspaceReveal(root, _retryReconciliation)
        }
      })
      .catch((error) => {
        console.warn('[workspace-project] core workspace load failed', error)
        if (_bootCurrent()) updateWorkspaceActivation(root, 'core', { state: 'error' })
        else _releaseCore()
      })

    // Resolve the tab's workspace before revealing anything from its scope.
    // After that, a slow module/layout/roster must not lock every shell action.
    // The work keeps its guarded continuations: pending is never stamped as an
    // empty result, saved layout is retained, and late success clears the notice.
    await _wsReady
    if (!_bootCurrent()) return
    let _revealTimer
    try {
      // The reveal waits for the workspace's own essentials only — the
      // takeover (+ the URL's app), the saved layout and the roster — never
      // for apps the page does not show (boot fix step 2).
      const _revealWork = Promise.all([_projectReveal, _prefsChain, _peopleChain])
      const ready = await Promise.race([
        _revealWork.then(() => true),
        new Promise((resolve) => {
          _revealTimer = setTimeout(() => resolve(false), _bootRevealBudgetMs)
        })
      ])
      if (!ready && _bootCurrent()) {
        const _slowStamp = updateWorkspaceActivation(root, 'core', { state: 'slow' })
        _coreStamp = _slowStamp
        console.warn(
          '[workspace-project] boot reveal budget elapsed; scoped data continues loading'
        )
        // The notice speaks for the page, never for apps still setting up
        // behind it (boot fix step 2). It waited for `_coreWork` — every app,
        // three payloads at a time — so a page that was ready kept saying
        // "still loading" until the last background app answered. Once the
        // reveal set lands, 'slow' steps back to the quiet loading state that
        // the final ready/error write replaces.
        _revealWork.then(
          () =>
            _bootCurrent()
              ? settleWorkspaceActivation(root, 'core', _slowStamp, {
                  state: 'loading',
                  visible: !!_opts.background
                })
              : _releaseCore(),
          () => {}
        )
      }
    } finally {
      clearTimeout(_revealTimer)
    }
    if (!_bootCurrent()) return
    if (
      _peopleErr &&
      /no active workspace for user/i.test(_peopleErr?.message || String(_peopleErr))
    )
      return

    // Team helpers also publish partial results before their promises settle.
    // Keep those writes under this load's ownership, including member maps.
    // Capture claims so a switch during SDK initialization cannot change the
    // request's org set after loadAllTeams has chosen its cache key. Read the
    // maps live so concurrent member responses still merge with one another.
    const _teamRoot = {
      jwtClaims: {
        workspace_org_id: root.jwtClaims?.workspace_org_id,
        orgs: (root.jwtClaims?.orgs || []).map((org) => ({ ...org }))
      },
      get teams() {
        return root.teams
      },
      get teamsByEmail() {
        return root.teamsByEmail
      },
      get teamsByUserId() {
        return root.teamsByUserId
      },
      get teamMembersLoaded() {
        return root.teamMembersLoaded
      },
      update: (patch) => {
        if (_rosterCurrent()) root.update(patch)
      }
    }

    // A person's `status` is cap-table / HR state — the equity vesting trigger
    // (migration 0128) flips it to 'left' for class-B holders past vesting_end.
    // It is NOT a workspace-membership signal. Workspace access is gated by auth
    // (the getMe auth gate), so we must NOT log the user out based on cap-table
    // status: doing so signed out active members (incl. a founder marked 'left'
    // by vesting) and caused an iPad refresh loop. The 'left'/'inactive' status
    // only affects the people / cap-table views (peopleGroups, peopleUtils).
    // Non-blocking boot: fire the secondary fan-out and DON'T await it. Every
    // call self-hydrates via .then(root.update) and nothing downstream uses the
    // results, so awaiting only delayed boot completion by the slowest call
    // (~10s each on a cold backend). Returning here lets the shell + sidebar
    // render in ~1s and each section fills in as its data lands, instead of a
    // blank/"Thinking…" gate for the whole fan-out. (`_shellLoadInFlight` resets on
    // function exit below — acceptable; loadPrivateData only re-fires on auth
    // events, and the roster fetch above is deduped in listOrgMembers.)
    void Promise.all([
      _loadRoles(root, sdk),
      // Credit guard — powers CreditBlocker/CreditBanner + the admin
      // threshold notifications. Self-hydrates `_creditGuard`; errors are
      // swallowed into an { error } payload (a metering outage must never
      // gate the workspace).
      loadCreditGuard(root, sdk),
      sdk
        .execute('workspaceProject.announcements', 'list', { order: 'date' })
        .then((data) => {
          if (!data) return
          root.update({
            announcements: data.map((r) => ({
              id: r.id,
              title: r.title,
              author: r.author,
              // Same split as _person's avatar: URL-or-empty, letters in
              // their own field (a non-URL authorAvatar 404ed via the
              // byline <img> src on every render; a stored non-URL reads as
              // nothing).
              authorAvatar:
                typeof r.author_avatar === 'string' && /^(https?:|\/)/.test(r.author_avatar)
                  ? r.author_avatar
                  : '',
              authorInitials: initials(r.author),
              authorRole: r.author_role,
              date: r.date,
              readTime: r.read_time,
              tag: r.tag,
              cover: r.cover,
              excerpt: r.excerpt,
              body: r.body,
              reactions: r.reactions || {},
              audience: r.audience === 'external' ? 'external' : 'internal',
              externalUrl: r.external_url || r.externalUrl || ''
            }))
          })
        })
        .catch((e) => console.warn('[workspace-project] announcements fetch failed', e)),
      // intranet `stories` removed — the StoriesWidget renderer was deleted
      // 2026-06-02; root.stories had no consumer left (dead boot fetch). Table
      // dropped via 0162_drop_dead_stories.sql.
      // resource_links migrated to Mongo at /resource-links/*.
      sdk
        .execute('resourceLinks', 'list', {})
        .then((res) => {
          const data = Array.isArray(res) ? res : res?.data || []
          if (data?.length) root.update({ resourceLinks: data })
        })
        .catch((e) => console.warn('[workspace-project] resource_links fetch failed', e)),
      (_rosterCurrent() ? loadAllTeams(_teamRoot) : Promise.resolve(null))
        .then((result) => {
          if (!_rosterCurrent()) return
          const { teams = [], orgMembersByOrgId = {} } = result || {}
          const people = filterToActiveOrg(root.peopleAll || [], {
            ...root,
            orgMembersByOrgId
          })
          // Refresh the cheap, always-current bits every fire. We deliberately
          // do NOT overwrite `teamsByEmail` here: loadAllTeams returns it EMPTY
          // (it defers the per-team member fetch), so spreading it used to WIPE
          // the populated map on every loadPrivateData fire — and loadPrivateData
          // re-fires on every auth event (sign-in, ~hourly token refresh) via
          // subscribeAuthSession. Combined with the now-working `teamMembersLoaded`
          // guard, dropping the wipe means the per-team member fan-out below runs
          // ONCE and is skipped on subsequent fires (instead of storming
          // GET /organizations/:org/teams/:team/members each time). An org switch
          // wipes teamsByEmail + teamMembersLoaded in onWorkspaceChange, so the
          // new org still re-fans-out.
          _teamRoot.update({ teams, orgMembersByOrgId, people })
          // loadAllTeams returns teams + org-membership, but NOT the member→team
          // mapping (teamsByEmail). Without it, /people shows every member as
          // "Uncategorized" and /team-and-agents can't render team GroupFrames.
          // Each loadTeamMembers is idempotent via `teamMembersLoaded`, so this
          // only hits the network for teams not yet loaded this session.
          if (!_rosterCurrent()) return
          return loadAllTeamMembers(_teamRoot).then(() => {
            if (_rosterCurrent()) root.update({ teamsByEmailLoaded: true })
          })
        })
        .catch((e) => console.warn('[workspace-project] teams load failed', e)),
      // user_documents migrated to Mongo as `docs` with type='user_document'.
      // sdk.docs.userDocs.list() returns {data, count} envelope.
      shellIsCommHost()
        ? null
        : sdk
            .execute('docs.userDocs', 'list', {})
            .then((res) => {
              const data = Array.isArray(res) ? res : res?.data || []
              if (data?.length) root.update({ userDocs: data })
            })
            .catch((e) => console.warn('[workspace-project] user_documents fetch failed', e)),
      // Dashboard prefs gate reveal above; account prefs refresh concurrently.
      fetchOrgNotifCount(root).catch((e) =>
        console.warn('[workspace-project] org-notifications fetch failed', e)
      ),
      // Calendar window: ±1 day past, +14 days future. Mirrors the
      // onWorkspaceChange refetch — without it, cold sign-in leaves
      // intranetEvents empty until the user switches orgs.
      (() => {
        const min = new Date(Date.now() - 1 * 86400000).toISOString()
        const max = new Date(Date.now() + 14 * 86400000).toISOString()
        return sdk
          .execute('workspaceProject.calendar', 'list', {
            filter: { deleted: false, start_at: { gte: min, lte: max } },
            order: 'start_at',
            limit: 500
          })
          .then((data) => {
            if (!data) return
            const me = root.currentUserEmail || ''
            const byKey = new Map()
            for (const r of data) {
              const key = r.google_event_id || `local-${r.id}`
              const existing = byKey.get(key)
              if (!existing) {
                byKey.set(key, r)
                continue
              }
              if (r.google_calendar_id === me && existing.google_calendar_id !== me) {
                byKey.set(key, r)
              }
            }
            root.update({
              intranetEvents: [...byKey.values()].map((r) => ({
                id: r.id,
                title: r.title,
                startAt: r.start_at,
                endAt: r.end_at,
                location: r.description ? r.description.slice(0, 60) : '',
                // Carry the FULL description + invitees through so the home
                // "Coming up" event modal shows them directly. The modal's
                // lazy fetchEventDetails 404s on home (no GET
                // /calendar/events/:id route — only `list` exists) and
                // root.events is empty here, so this seed is the only source
                // (2026-06-08 per Nika "data not loading in Coming up event
                // modals"). The list response already includes both fields.
                description: r.description ?? null,
                invitees: Array.isArray(r.invitees) ? r.invitees : [],
                attendees: Array.isArray(r.invitees) ? r.invitees : [],
                organizerEmail: r.organizer_email,
                googleEventId: r.google_event_id
              })),
              // The window answered: an empty list is now a real "nothing"
              // (the home Next up widget's empty sentence waits for this).
              intranetEventsLoaded: true,
              intranetEventsError: false
            })
          })
          .catch((e) => {
            console.warn('[workspace-project] calendar fetch failed', e)
            root.update({ intranetEventsError: true })
          })
      })(),
      // birthdays: roster-derived (the dedicated table was retired, mig 0160)
      // — derived from the
      // roster's person.birthday, no network (the home widget already
      // filters against root.people). Shared with onWorkspaceChange's roster
      // put (WS-SWITCH-INCOMPLETE-1 step 8): `birthdays` is a PROJECTION of
      // the scoped roster, so it is wiped with it and re-derived wherever the
      // roster is re-put.
      Promise.resolve().then(() => {
        root.update({ birthdays: _deriveBirthdays(root.people || root.peopleAll) })
      }),
      // company info: Mongo workspace.settings.companyInfo is the home now
      // (the dedicated company_info table is retired with the
      // workspace-project org; admins write it via updateWorkspaceSettings).
      // Kept as an array for the /company page's legacy row shape.
      Promise.resolve().then(async () => {
        // §5 RENDER site (the /company page paints these rows). Read through
        // THE accessor so a list-shaped record cannot pass as "loaded, this
        // workspace simply has no company info" — the direct `?.settings` read
        // made those indistinguishable, and the difference decides whether the
        // fetch fallback below runs at all. `ready === false` now positively
        // triggers the fetch instead of relying on `settings` happening to be
        // undefined.
        const { settings: heldSettings, ready, id: heldId } = shellWorkspaceSettings(root)
        let settings = ready ? heldSettings : null
        const wsId = heldId
        if (!settings && wsId && typeof sdk.getWorkspace === 'function') {
          try {
            const ws = await getWorkspaceCached(sdk, wsId)
            settings = ws?.settings
          } catch (e) {
            console.warn('[workspace-project] company-info settings fetch failed', e)
          }
        }
        const info = settings?.companyInfo
        const rows = Array.isArray(info) ? info : info ? [info] : []
        root.update({ companyInfo: rows })
      })
    ])
    // Warm-start snapshot write (tickets/workspace.md "boot: scope-keyed
    // warm-start snapshot"). Only at a FULLY settled boot — workspaceReady
    // means `root.workspace` is the complete record (not a list/claims-shaped
    // stub, see activeWorkspaceRecord.js), and `!scopeStale(_loadWsGen)`
    // means no NEWER switch has superseded this pass. Writing from a
    // superseded pass would tag content loaded for one scope under whatever
    // (org, workspace) happens to be live on `root` by the time this runs —
    // skip entirely rather than risk a mismatched write; the pass that
    // actually owns the current scope will write its own snapshot when IT
    // settles. See bootSnapshot.js for the isolation contract; the
    // consuming (paint-from-cache) half is wired too now — see that
    // file's `resolveWarmBootSnapshot` and bootShell.js's warm-start
    // block (tickets/opus.md "boot: the scope-keyed warm-start snapshot's
    // PAINT half was never built").
    if (!scopeStale(_loadWsGen) && root.workspaceReady && root.org?.id && root.workspace?.id) {
      try {
        writeBootSnapshot({
          userId: root.currentUserId,
          orgId: root.org.id,
          workspaceId: root.workspace.id,
          currentUserEmail: root.currentUserEmail,
          currentUserUsername: root.currentUserUsername,
          org: root.org,
          orgs: root.orgs,
          workspace: root.workspace,
          workspaces: root.workspaces,
          members: root.peopleAll,
          // The skin the shell stamps for this scope — the SAME rule as app.js
          // _syncShellSkin — so the next warm boot stamps it before its first
          // paint (index.js applyWarmBootSkin). Null (a configured module not
          // loaded yet) keeps the row's last skin.
          workspaceSkin: resolveWorkspaceSkin(root, {
            info: shellWorkspaceSettings(root),
            brandedHost: isCustomDomainHost()
          }).skin
        })
      } catch (err) {
        console.warn('[loadPrivateData] boot-snapshot write failed:', err?.message || err)
      }
    }
  } finally {
    // Release the pass the moment its own reveal settles: the reveal budget
    // above and retryWorkspaceActivation both rely on a direct call starting
    // a fresh pass while slow chains keep their guarded continuations. A pass
    // whose latch a session end already released leaves the latch and the
    // rerun queue alone: they belong to the next session's pass.
    const _ownsLatch = _shellLoadInFlight === _loadLatch
    if (_ownsLatch) _shellLoadInFlight = null
    endDataLoad(root, _dlEpoch)
    _mark('wsboot:priv:end')
    _measure('wsboot:people', 'wsboot:priv:start', 'wsboot:priv:people-done')
    _measure('wsboot:project-extra-wait', 'wsboot:priv:people-done', 'wsboot:priv:project-done')
    _measure('wsboot:prefs-extra-wait', 'wsboot:priv:project-done', 'wsboot:priv:prefs-done')
    _measure('wsboot:total', 'wsboot:priv:start', 'wsboot:priv:end')
    // endDataLoad can synchronously start another accepted pass. That pass
    // owns any queue it creates, so this old finally must leave it alone.
    if (_ownsLatch && _loadRerunQueued && !_shellLoadInFlight) {
      const queued = _loadRerunQueued
      const stillCurrent = () =>
        _loadRerunQueued === queued &&
        queued.generation === scopeGen() &&
        queued.epoch === _dataLoadEpoch &&
        (!queued.element ||
          (queued.root.__element === queued.element &&
            !queued.element.__destroyed &&
            queued.element.__ref !== null))
      if (!stillCurrent()) {
        _loadRerunQueued = null
      } else if (
        queued.root !== root ||
        queued.element !== _loadElement ||
        _fetchScope(queued.root, queued.sdk) !== _loadScopeAtStart
      ) {
        // Hand off only after this pass's already-started chains settle, so
        // two passes never register modules or reconcile preferences at once.
        // A direct call in the meantime replaces the queued record (above).
        const chains = [_projectChain, _prefsChain, _peopleChain]
        const dropped = () => {
          if (!stillCurrent() || _shellLoadInFlight) {
            if (_loadRerunQueued === queued) _loadRerunQueued = null
            return true
          }
          return false
        }
        setTimeout(async () => {
          if (dropped()) return
          await Promise.allSettled(chains)
          if (dropped()) return
          _loadRerunQueued = null
          return loadPrivateData(queued.root, queued.sdk, queued.opts).catch((err) =>
            console.warn('[workspace-project] queued private-data load failed', err)
          )
        }, 0)
      } else {
        // A same-root scope correction returned to the accepted scope before
        // completion. No duplicate load is needed, but its held epoch is ours.
        _loadRerunQueued = null
        endDataLoad(queued.root, queued.epoch)
      }
    }
  }
}

// The switch epoch, exported so ANY workspace-scoped async adopter (AI
// threads, records seeds, page loaders) can join the same supersession guard:
// capture `scopeGen()` before the request, drop the response when
// `scopeStale(gen)` — a late reply from the previous tenant must never
// repaint the current one.
export const scopeGen = () => _wsChangeGen
export const scopeStale = (gen) => gen !== _wsChangeGen

// A session end supersedes every pass of the session it ends
// (WS-SIGNIN-DOES-NOT-SUPERSEDE-INFLIGHT-SWITCH-1). Only a newer switch moved
// the switch epoch, and a sign-out or another person's sign-in is not a
// switch: the cross-tab sign-out (shared switchers/logout.js) keeps the page,
// the next sign-in on /login is an SPA one, so an old switch's late answers
// (the account's preferences row, the roster, every PHASE-2 list) and every
// scopeGen() adopter's still landed in the NEXT person's root. Moving the
// epoch here drops them all at their own guards (`put`, `scopeStale`, the
// boot pass's `_bootCurrent`). The loader claim of the ended session is
// released quietly: a superseded switch's watchdog would otherwise surface
// "the switch is slow" over a signed-out shell. So is the in-flight latch of
// its boot pass (and its queued rerun): with the epoch moved, the latch would
// drop the next session's boot call as one a switch already owns, and no
// switch owns the next session's scope. The old pass runs on with every write
// stale, and its `finally` leaves the next pass's latch alone. Released before
// the loader claim: a clear can start the next pass synchronously. Called by
// auth.js subscribeAuthSession on a real session change only, never on the
// first sign-in of a page (an early adopter's answer belongs to that session).
export const endWorkspaceSession = (root) => {
  _wsChangeGen++
  if (!root) return
  if (_shellLoadInFlight?.root === root) _shellLoadInFlight = null
  if (_loadRerunQueued?.root === root) _loadRerunQueued = null
  endDataLoad(root, _dataLoadEpoch)
}

// `birthdays` is a projection of the roster (person.birthday), no network. One
// derivation, two call sites: the boot load above and the switch's roster put
// below (WS-SWITCH-INCOMPLETE-1 step 8 — the key is WORKSPACE_SCOPED, wiped in
// PHASE 1 with its source, so PHASE 2 must re-derive it or the home widget
// stays empty until a full remount).
const _deriveBirthdays = (list) =>
  (list || [])
    .filter((p) => p && p.birthday)
    .map((p) => ({ name: p.name, email: p.email, birthday: p.birthday }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

// `opts.onWorkspaceResolved(workspace)` — OPTIONAL callback, fired the moment
// this pass settles the workspace record it is switching INTO (the adoption
// below), NOT when the whole pipeline finishes. The org-switch handler in
// app.js uses it to stamp the address bar at the switch's own resolve point:
// waiting for this function's promise cost ~9 s of a URL that still named the
// OLD org, and a reload inside that window flipped the tab back
// (WS-ORG-SWITCH-URL-SYNC-LANDS-LATE-1). Called at most once per pass, never
// for a superseded pass, and every throw is contained here — a URL side
// effect must never abort the refetch pipeline.
export const onWorkspaceChange = async (root, sdk, _newClaims, opts = {}) => {
  if (!root || !sdk) return
  // See loadPrivateData's identical wrapper above — root.call keeps this
  // reaching the one live getWorkspaceCached singleton instead of a raw
  // cross-file import frank cannot resolve once serialized.
  // HARDENED (P1 lead, 2026-08-15): `root.call` is ABSENT on some state
  // objects mid-switch — bellf-qa captured the TypeError thrown from THIS
  // wrapper synchronously inside onCreate (rootBus replays orgSwitched to a
  // late subscriber during createDomqlElement), and an uncaught lifecycle
  // throw aborts element creation mid-tree, leaving later handlers unbound.
  // Fallback: the direct import reaches the SAME in-bundle singleton (cache
  // intact); under frank revival the import vanishes but `typeof` of the
  // free identifier is safely 'undefined' and we degrade to a null read
  // instead of a throw.
  const getWorkspaceCached = (...args) =>
    typeof root.call === 'function'
      ? root.call('getWorkspaceCached', ...args)
      : typeof _gwcDirect === 'function'
        ? _gwcDirect(...args)
        : Promise.resolve(null)
  // Supersession guard — see _wsChangeGen. A newer switch increments the
  // generation; this (older) run then stops writing at its next checkpoint and
  // cedes the loader, so last-SWITCH-wins instead of last-WRITE-wins.
  const myGen = ++_wsChangeGen
  const superseded = () => myGen !== _wsChangeGen
  // Hand the switch's resolved workspace identity to the caller the INSTANT
  // this pass owns it — see the `onWorkspaceResolved` note on this function.
  // Idempotent by contract (the shell's URL sync no-ops on an already-correct
  // URL), so the two call sites below — the bare-org list row and the record
  // adoption — may both fire; the earlier one is what removes an RTT from the
  // window in which the address bar still names the OLD org.
  const notifyWorkspaceResolved = (ws) => {
    if (superseded() || typeof opts?.onWorkspaceResolved !== 'function') return
    try {
      opts.onWorkspaceResolved(ws || null)
    } catch (err) {
      console.warn('[shell] onWorkspaceResolved callback failed:', err?.message || err)
    }
  }
  // Epoch-guarded state write: a superseded run drops its patch so an in-flight
  // PHASE-2 fetch from an older switch can't repaint stale data over the current.
  const put = (patch) => {
    if (superseded()) return
    try {
      root.update(patch)
    } catch (_) {
      /* torn down mid-switch */
    }
  }
  // Keep the boot-style MainLoader up for the WHOLE reload. `_wsSwitching`
  // (raised by _startSwitching) is cleared by _endSwitching the instant
  // sdk.switchOrg resolves — long before this refetch finishes — so without a
  // flag this function owns, the UI sits idle over stale/empty data for the
  // 2-6s hydration (the "takes long to load" symptom). `_dataLoading` is
  // already in _isLoaderActive; raise it now via the ownership epoch — a boot
  // loadPrivateData's `finally` landing mid-switch can no longer clear it —
  // and only the current (non-superseded) switch clears it at the reveal.
  const _dlEpoch = beginDataLoad(root)
  shellClearWorkspaceSwitchFallback()

  // Per-run phase table + bounded-phase runner — see the instrumentation note
  // above `_switchRevealBudgetMs`. `_t0` is this pass's origin; every phase
  // draws from the SAME remaining budget, so the reveal is always reached
  // before the watchdog can fire on a stalled await inside a phase.
  const _t0 = _nowMs()
  const _phases = []
  const _publishPhases = () => {
    try {
      // Direct assignment, never `root.update` — a diagnostic must not
      // schedule a render or collide with the epoch-guarded writes.
      root.__wsSwitchPhases = _phases
    } catch (_) {
      /* frozen/torn-down root — diagnostics are never load-bearing */
    }
  }
  const _phaseTable = () =>
    _phases
      .map(
        (p) =>
          p.phase + '=' + Math.round(p.ms) + (p.boundedOut ? '(BOUNDED)' : p.error ? '(ERR)' : '')
      )
      .join(' ')
  const _remainingBudget = () => Math.max(0, _switchRevealBudgetMs - (_nowMs() - _t0))
  // Runs `thunk`, records its duration, and resolves with `fallback` if the
  // remaining reveal budget elapses first. NEVER throws: a phase failure is a
  // recorded, non-fatal continuation, exactly like the try/catch each of these
  // awaits already carried.
  const _phase = async (label, thunk, fallback = null) => {
    const started = _nowMs()
    const record = (extra) => {
      const row = { phase: label, ms: _nowMs() - started, at: started - _t0, ...extra }
      _phases.push(row)
      _publishPhases()
      if (row.boundedOut) {
        console.warn(
          '[shell] workspace-switch phase "' +
            label +
            '" exceeded the reveal budget after ' +
            Math.round(row.ms) +
            'ms — continuing without it (the request keeps running). ' +
            'Phases so far: ' +
            _phaseTable()
        )
      } else if (root.__wsSwitchDebug) {
        console.debug(
          '[shell] workspace-switch phase',
          label,
          Math.round(row.ms) + 'ms',
          extra?.error || ''
        )
      }
      return row
    }
    let work
    try {
      work = Promise.resolve(thunk())
    } catch (err) {
      record({ error: err?.message || String(err) })
      return fallback
    }
    // A bounded-out phase's promise keeps running — swallow its late rejection
    // so it can never surface as an unhandled rejection after we moved on.
    work.catch(() => {})
    const budget = _remainingBudget()
    let timer = null
    const bounded = new Promise((resolve) => {
      timer = setTimeout(() => resolve(_BOUNDED), budget)
      if (timer && typeof timer.unref === 'function') timer.unref()
    })
    let out
    try {
      out = await Promise.race([work, bounded])
    } catch (err) {
      clearTimeout(timer)
      record({ error: err?.message || String(err) })
      return fallback
    }
    clearTimeout(timer)
    if (out === _BOUNDED) {
      // Log the late settle too — the next reader learns the real duration of
      // the offending call, not just "over budget".
      const _lateStart = started
      work.then(
        () =>
          console.warn(
            '[shell] workspace-switch phase "' +
              label +
              '" finally settled after ' +
              Math.round(_nowMs() - _lateStart) +
              'ms'
          ),
        () => {}
      )
      record({ boundedOut: true })
      return fallback
    }
    record({})
    return out
  }

  // Bounded watchdog — see the FABLE P1 note above `_wsChangeGen`. Fires purely
  // off the epoch (not off this function's own execution), so a stalled await
  // ANYWHERE below can't block it. No-ops the instant a newer switch supersedes
  // this epoch, or once this run's own reveal clears it below — `endDataLoad`'s
  // existing epoch + `_dataLoadClearedFor` checks make a redundant/late fire
  // here a harmless no-op, so nothing needs to explicitly disarm it on every
  // early-return path.
  const _wsChangeWatchdog = setTimeout(() => {
    if (_dataLoadEpoch !== _dlEpoch || _dataLoadClearedFor === _dlEpoch) return
    console.error(
      '[shell] onWorkspaceChange exceeded its bounded window (' +
        _wsChangeWatchdogMs +
        'ms) without settling — forcing the loader clear + surfacing an error ' +
        'instead of wedging. Activation is still incomplete; a pending request or ' +
        'delayed main-thread timer may have exceeded the budget. Phases: ' +
        (_phaseTable() || '(none recorded — the stall is BEFORE the first instrumented phase)')
    )
    endDataLoad(root, _dlEpoch, { _workspaceSwitchError: true })
    try {
      root.call?.('openNotification', {
        title: root.call?.('polyglot', 'workspace.switch.slow'),
        message: root.call?.('polyglot', 'workspace.switch.slowBody'),
        type: 'error'
      })
    } catch (_) {
      /* the notification surface isn't reachable from this context */
    }
  }, _wsChangeWatchdogMs)
  if (_wsChangeWatchdog && typeof _wsChangeWatchdog.unref === 'function') {
    _wsChangeWatchdog.unref()
  }

  // Close the previous workspace's chat realtime stream — it keeps
  // delivering the OLD tenant's events otherwise (the handle is workspace-
  // keyed; subscribeRealtime re-opens it for the new workspace on the next
  // chat surface touch). Direct teardown, no chat-package import needed.
  const chatRt = root._chatRealtimeChannel
  if (chatRt) {
    try {
      chatRt.unsubscribe?.()
    } catch (_) {
      /* already closed */
    }
    root._chatRealtimeChannel = null
  }
  // The org-members cache + in-flight promise are not org-keyed — a switch
  // within the 15s TTL (or during an in-flight fetch under the old token)
  // would repopulate root.people with the PREVIOUS org's roster.
  invalidateOrgMembers()

  // P2/P3 — start the two most-visible, module-INDEPENDENT reads NOW, in
  // parallel with getWorkspace + the module load below, instead of dead last.
  // The roster (home greeting / avatars / assignee pickers) and teams are what
  // the UI reveals with; both need only the active-org context switchOrg already
  // set (listOrgMembers is org-keyed + just invalidated; loadAllTeams reads only
  // jwtClaims.orgs and is cached). Awaited + put post-wipe as the ESSENTIAL
  // set below, so the loader clears once they land — the switch reveals in ~1 RTT
  // instead of waiting on the slowest of ~18 fetches.
  if (superseded()) return
  // The shared helpers publish early teams and delayed member maps directly.
  // Guard those writes with the same generation as the final result. Capture
  // claims before the SDK await so the helper's cache key and org reads agree.
  // Keep maps live so current responses merge and loaded-team guards still work.
  const _teamRoot = {
    jwtClaims: {
      workspace_org_id: root.jwtClaims?.workspace_org_id,
      orgs: (root.jwtClaims?.orgs || []).map((org) => ({ ...org }))
    },
    get teams() {
      return root.teams
    },
    get teamsByEmail() {
      return root.teamsByEmail
    },
    get teamsByUserId() {
      return root.teamsByUserId
    },
    get teamMembersLoaded() {
      return root.teamMembersLoaded
    },
    update: put
  }
  const _peoplePromise = shellListOrgMembers(
    sdk,
    root.workspace?.id ? String(root.workspace.id) : null
  ).catch(() => null)
  const _teamsPromise = loadAllTeams(_teamRoot).catch(() => ({}))

  // Stamp the workspace into the analyzing tracker so subsequent
  // envelopes ship with workspace_id set. Without this, the tracker's
  // beforeSend (which drops envelopes with no scope) silently swallows
  // every event on signed-in pages until the user explicitly switches
  // orgs — `reidentifyOnOrgSwitch` only fires on sdk.orgSwitched, not
  // on initial sign-in. Read the workspace id from the freshly-resolved
  // JWT claims first, fall back to root.org for safety.
  // ONE resolver, shared with boot + the orgSwitched handler
  // (ANALYZING-SESSION-ROTATE-1): sdk._context.activeWorkspaceId first (the
  // authoritative local truth — `_newClaims.active_workspace_id` can be a
  // STALE re-mint from the previous org, see activeWsId below), then the
  // tab's workspace, then claims, then the org id. A stamp change rotates
  // the analyzing session, so all writers must agree on the id.
  const wsId =
    resolveAnalyzingScopeId({ sdk, root, claims: _newClaims }) || _newClaims?.workspace_id || null
  if (wsId) setAnalyzingWorkspace(wsId)

  // Snapshot which optional caches were previously populated. The wipe
  // below sets these to their schema empty state, so the in-place
  // conditional refetches in PHASE 2 wouldn't otherwise know whether
  // to fire (eg. tickets/myTickets/feedItems and chat caches are only
  // populated when the user has visited that page this session).
  const hadTicketsBoard = Array.isArray(root.tickets) && root.tickets.length > 0
  const hadMyTickets = Array.isArray(root.myTickets)
  const hadFeedItems = Array.isArray(root.feedItems)
  const hadChannels = Array.isArray(root.channels) && root.channels.length > 0
  const hadChatMessages = root.messagesByChannel && Object.keys(root.messagesByChannel).length > 0
  // aiChat threads are WORKSPACE-scoped (2026-07-14 per Nika: "aiChat
  // threads are WORKSPACE-scoped" — mirrors the chat isolation switch
  // above). Snapshot BEFORE `root.replace` wipes `_aiThreads` to its
  // schema empty state below, so PHASE 2 knows whether to refetch for
  // the NEW workspace. Also true when the assistant panel is open even
  // with zero threads yet (matches the chatSurfaceActive convention).
  const hadAiThreads =
    (Array.isArray(root._aiThreads) && root._aiThreads.length > 0) || !!root.assistantOpen
  // WS-SWITCH-INCOMPLETE-1 step 8 (bucket E): the SIMONE conversation core
  // (`sessions`/`chatMessages`/`activeSessionId`/…) and the ASK-USER decision
  // rows are WORKSPACE-scoped and wiped in PHASE 1 below. Both are seeded by
  // their surfaces ON MOUNT only (Chat.onCreate → hydrateServerSessions;
  // AppAssistant → fetchAskUserDecisions), so a same-route switch with the
  // assistant open would leave them empty until a full remount. Snapshot
  // BEFORE the wipe, same convention as `hadAiThreads` above.
  const hadSimoneSessions =
    (root.sessions && Object.keys(root.sessions).length > 0) ||
    (Array.isArray(root.chatMessages) && root.chatMessages.length > 0) ||
    !!root.assistantOpen
  const hadAskUserDecisions =
    (Array.isArray(root.askUserDecisions) && root.askUserDecisions.length > 0) ||
    !!root.assistantOpen
  // Route comparisons below run on the STRIPPED path (BOOT-RESHUFFLE F7):
  // MWT URLs carry the /w/<org>[/<ws-handle>] prefix, so comparing the raw
  // pathname against '/chat' / '/tickets' / … was dead on every prefixed tab —
  // a user sitting on /w/acme/tickets read as "not on tickets" and the switch
  // never refetched the very surface they were looking at.
  const _rawPath = typeof window !== 'undefined' ? window.location.pathname : ''
  const _path = shellStripWorkspacePrefix(_rawPath) || '/'
  const _slot = root._canvasSlot ? String(root._canvasSlot) : ''
  // The home dashboard is a surface too (F7): its widgets read channels and
  // documents straight off root — after the PHASE-1 wipe they stayed empty
  // until a full remount because home never counted as "active". (Tickets and
  // projects are excluded: the home page's own onUpdate refetches those on an
  // org change already.)
  const homeSurfaceActive = _path === '/' || _path === ''
  // A user sitting ON the chat surface must get the new workspace's chat
  // even when the previous workspace had ZERO channels (hadChannels false
  // used to gate the refetch off entirely — switching into a workspace
  // from an empty-chat one left the sidebar blank until a full remount).
  const chatSurfaceActive = _path === '/chat' || root._canvasSlot === '/chat'
  // Calendar / Meet page caches (separate from the workspace-shell's
  // `intranetEvents` summary — these are the page-level state slots
  // populated by the pages' own `fetch: [...]` declarations on mount).
  const hadCalendarEvents = Array.isArray(root.events) && root.events.length > 0
  const hadMeetRooms = Array.isArray(root.rooms) && root.rooms.length > 0
  const hadMeetWaitingRoom = Array.isArray(root.waitingRoom)
  // P4 — tickets/calendar/meet get the SAME route-active escape hatch chat has:
  // a same-route SPA switch never remounts the page, so gating the refetch purely
  // on `had*` (was the PREVIOUS workspace populated) leaves the surface BLANK
  // after switching INTO a workspace that has data. If the user is sitting on the
  // surface, always refetch. (_path/_slot declared with the chat block above.)
  const ticketsSurfaceActive =
    _path.startsWith('/tickets') ||
    _path.startsWith('/tasks') ||
    _slot.startsWith('/tickets') ||
    _slot.startsWith('/tasks')
  const calendarSurfaceActive = _path === '/calendar' || _slot === '/calendar'
  const meetSurfaceActive = _path === '/meet' || _slot === '/meet'
  // Documents / files desktop (2026-07-30 isolation audit #2): these keys
  // joined the wipe registry, so a mounted surface needs the same
  // had-or-active refetch convention — the fetch plugin is create-only and
  // would otherwise leave the page BLANK after the wipe until a remount.
  const hadDocuments =
    !!root._documentsLoaded ||
    (Array.isArray(root.documents) && root.documents.length > 0) ||
    (Array.isArray(root.notes) && root.notes.length > 0) ||
    (Array.isArray(root.kbArticles) && root.kbArticles.length > 0)
  const documentsSurfaceActive =
    _path === '/documents' || _path.startsWith('/documents/') || _slot.startsWith('/documents')
  const hadFileNodes =
    !!root._fileNodesLoaded || (Array.isArray(root.fileNodes) && root.fileNodes.length > 0)
  const filesSurfaceActive = _path === '/files' || _slot === '/files'
  const hadUserPrefs = root.userPrefs != null
  const hadCompanyInfo = Array.isArray(root.companyInfo) && root.companyInfo.length > 0
  const companySurfaceActive = _path === '/company' || _slot === '/company'
  // Profile surface (`/user/:slug`, `/u/:slug`). The PHASE-1 wipe drains
  // `_profileViewed` + `_profileActivity` (workspace/scopes.js bucket ⑦ — a
  // roster row and a per-workspace heatmap, both tenant data), and a
  // SAME-ROUTE switch never remounts the page, so without this pair the page
  // kept its empty shell until a navigation re-ran `onCreate`. Same
  // had-or-active convention as every surface above; there is no `had*` term
  // because the wipe already ran when this is read — the route IS the signal.
  const profileSurfaceActive = isUserProfilePath(_path) || isUserProfilePath(_slot)
  // History app (/history): its audit-log rows, load marker and error key are
  // wiped in PHASE 1 (state-app.js) and read imperatively from the page's own
  // onCreate, which a same-route switch never re-runs. Same route-is-the-signal
  // convention as the surfaces above — and either pane of a split can host it.
  const historySurfaceActive = [_path, _slot, root._canvasSlotSecond].some(isHistoryPath)
  // Getting started → Team (/getting-started/team, POB S9): the found people,
  // the owner's drafts and the domain switch are wiped in PHASE 1 and read
  // from the page's own onCreate — the History case exactly.
  const gettingStartedSurfaceActive = [_path, _slot, root._canvasSlotSecond].some(
    (href) => gettingStartedSurfaceFor(href) === 'team'
  )
  // Getting started → Imports (/getting-started/imports, POB S10): the same
  // rule for the found sources, the review and the run.
  const gettingStartedImportsActive = [_path, _slot, root._canvasSlotSecond].some(
    (href) => gettingStartedSurfaceFor(href) === 'imports'
  )

  // Warm-start snapshot purge (tickets/workspace.md "boot: scope-keyed
  // warm-start snapshot") — the OUTGOING (userId, workspaceId) scope's cached
  // chrome is dropped BEFORE the wipe below touches `root.workspace`, mirroring
  // every other PHASE-1 wipe's "clear before the network round trip" timing.
  // Belt-and-suspenders: a different tenant reads a DIFFERENT storage key by
  // construction and could never paint this one, but "switching tenants
  // purges it" is an explicit acceptance line — honor it literally, not just
  // by relying on the key never colliding.
  if (root.currentUserId && root.workspace?.id) {
    try {
      purgeBootSnapshot({ userId: root.currentUserId, workspaceId: root.workspace.id })
    } catch (_) {
      /* best-effort */
    }
  }

  // LIVEKIT TEARDOWN — RUNS BEFORE PHASE 1, AND THE ORDER IS THE FIX
  // (MEET-LIVE-CALL-SURVIVES-SWITCH-1). The LiveKit `Room` is a MODULE
  // singleton in meet/functions/connectCall.js, outside state, so it survives
  // every state operation: without this, a call joined in workspace A kept
  // publishing microphone and camera into A's room while B's UI was on screen.
  // The four keys that MIRROR that singleton (`livekitConnected`,
  // `livekitParticipants`, `livekitSpeakers`, `meetChatMessages`) were held OUT
  // of the wipe registry for exactly that reason — wiping the mirror first
  // hides CallWidget over a room that is still live, which is the wrong state,
  // not a fix. They join the registry in the SAME diff as this teardown and
  // only make sense together; neither half may ship alone.
  //
  // `disconnectCall` rides meet's EAGER function slice (meet/functions/
  // eagerSlice.js), so it is el.call-able from every route, not just /meet, and
  // it is idempotent: with no room it just resets the mirror keys. It is
  // reached through `root.__element` — the root STATE carries no `.call`, its
  // back-pointer element does (same handle the SIMONE PHASE-2 hydrate below
  // uses); a state-only `root.call` would resolve nothing and fail silently.
  //
  // BOUNDED await: the teardown is awaited so the room stops publishing BEFORE
  // the mirror is cleared, but a LiveKit `disconnect()` that never settles must
  // not hold the previous tenant's data on screen — that would trade this leak
  // for a worse one. Past the bound we log loudly and proceed; the disconnect
  // keeps running on its own.
  const _meetEl = root.__element
  if (_meetEl && typeof _meetEl.call === 'function') {
    try {
      const _torn = _meetEl.call('disconnectCall')
      if (_torn && typeof _torn.then === 'function') {
        let _t = null
        const _bounded = new Promise((resolve) => {
          _t = setTimeout(() => {
            console.error(
              '[shell] disconnectCall did not settle within ' +
                _livekitTeardownBoundMs +
                'ms of a workspace switch — wiping the call mirror anyway. The ' +
                'LiveKit room may still be publishing into the previous tenant.'
            )
            resolve()
          }, _livekitTeardownBoundMs)
        })
        await _phase('livekit-teardown', () =>
          Promise.race([_torn.then(() => clearTimeout(_t)).catch(() => clearTimeout(_t)), _bounded])
        )
      }
    } catch (err) {
      console.warn('[shell] disconnectCall on workspace switch failed:', err?.message || err)
    }
  }

  // VOICE TEARDOWN — the shell call panel's session (functions/calls/
  // workspaceCall.js) is a module singleton like the LiveKit room above: stop
  // it BEFORE the wipe clears `_callPanel`, so a call on the previous
  // workspace's number never stays connected behind the next tenant's UI.
  // Synchronous and idempotent; the next route sync starts the new
  // workspace's session.
  if (_meetEl && typeof _meetEl.call === 'function') {
    try {
      _meetEl.call('stopWorkspaceVoice')
    } catch (err) {
      console.warn('[shell] stopWorkspaceVoice on workspace switch failed:', err?.message || err)
    }
  }

  // PHASE 1 — EARLY, SYNCHRONOUS wipe: every org/workspace/workspace-project-
  // scoped slot resets to its schema empty state NOW, before the workspace-
  // record fetch below ever hits the network (per Nika 2026-08-01: "loading
  // old apps, personalized mongo or ai threads is super malicious"). The wipe
  // used to run AFTER that fetch (+ the bare-org list resolve), so for a full
  // network round-trip the PREVIOUS tenant's module apps, records and AI
  // threads stayed live in state behind (or worse, without) the loader. The
  // loader (_dataLoading) is already up — the user sees a clean transition,
  // never the old tenant.
  //
  // `root.replace` (vs `root.update`) does "remove old, change with new"
  // semantics — it clears every own prop then writes the new bag, so nested
  // objects like `teamsByEmail` and `orgMembersByOrgId` actually drop their
  // previous-org keys. We spread `root.parse()` first to preserve USER-scope
  // state (currentUserEmail, themeMode, lang, jwtClaims, signedIn, etc.) and
  // UI-only filter inputs so the user stays signed in and their view
  // configuration carries across the switch. `root.workspace` deliberately
  // carries through (not in the registry): blanking the active-workspace
  // record breaks chrome guards; the fetched record is adopted via `put`
  // right after the fetch resolves, before applyWorkspaceConfig reads it.
  root.replace({
    ...root.parse(),
    // Re-assert the fetch scope: smbls `parse()` omits `__`-prefixed keys, so
    // the `...root.parse()` spread above would otherwise DROP __fetchScope and
    // leave the cache tenant-blind for the rest of the session. Set it here to
    // the NEW tenant (root.org is already the new org, sdk._context the new ws).
    __fetchScope: _fetchScope(root, sdk),
    // Every org/workspace/project-scoped slot resets to its empty schema value,
    // derived from the SINGLE-SOURCE registry (scopes.js `workspaceScopedWipe`).
    // Add NEW scoped keys THERE, never inline here — an un-wiped scoped key
    // leaks the previous org's data across the switch. UI-only filter inputs are
    // intentionally absent from the registry so they carry through via the
    // `...root.parse()` spread above.
    // MODULE-declared scoped keys (manifest `scopedKeys`, WS-MODULE-SCOPED-
    // KEYS-CONTRACT-1) ride the same registry — `root` is passed so a
    // `prefix*` family resolves against the live keys.
    ...workspaceScopedWipe(root)
  })
  // PERSONA-5: purge the sessionStorage persona blob in the SAME synchronous
  // beat as the state wipe above — see the `clearPersonaSession` wrapper's
  // doc comment for why the state registry entry alone doesn't reach it.
  // Best-effort: a storage exception here must never abort the switch.
  try {
    shellClearPersonaSession()
  } catch (_) {
    /* locked storage — the workspace-mismatch guard in
       readPersonaWorkspaceToken still refuses the stale blob on read */
  }

  // Refetch the workspace we're switching INTO so `root.workspace` (and its
  // Mongo `settings` bag — the source of truth for the personalized skin +
  // navbar) matches the active workspace BEFORE applyWorkspaceConfig resolves.
  // Without this, root.workspace stays stale at the previous workspace and the
  // re-theme resolves the WRONG config — the switch persists in Mongo + re-mints
  // the JWT but the UI keeps the previous workspace's skin/navbar (the GreenIG
  // "it doesn't switch" bug). On fetch failure we keep the stale record rather
  // than blank the workspace.
  // Resolve the active workspace id. Prefer `sdk._context.activeWorkspaceId` —
  // `switchWorkspace` sets it (and persists it to Mongo) as the AUTHORITATIVE
  // local truth. The passed `_newClaims.active_workspace_id` can be a STALE
  // federation claim: a lagging/failed re-mint fires onWorkspaceChange a second
  // time carrying an old workspace id (we saw it land on a workspace from a
  // PREVIOUS org — "Symbols Intranet" / org 69fd0898 — after a clean switch into
  // GreenIG). Context-first means both the Mongo-native and the federated pass
  // resolve the same, correct workspace.
  const activeWsId =
    sdk._context?.activeWorkspaceId ||
    _newClaims?.active_workspace_id ||
    root.jwtClaims?.active_workspace_id ||
    null
  // Mirror the authoritative id into state IMMEDIATELY — chat scoping
  // (chatUtils.getActiveWorkspaceId) and the realtime stream key read
  // root.activeWorkspaceId first, so every PHASE 2 refetch + page fetch
  // that races the (possibly never-landing) federated JWT re-mint still
  // scopes to the workspace actually switched into. This is also the key
  // subscribeRealtime historically expected but nothing ever wrote.
  if (activeWsId) {
    // Also stamp the fetch scope so the @symbo.ls/fetch cache + dedup keys
    // re-scope to the new tenant BEFORE any PHASE-2 or freshly-mounted-page
    // fetch runs — otherwise the tenant-blind cache serves the previous org's
    // rows back into the new view.
    put({ activeWorkspaceId: activeWsId, __fetchScope: _fetchScope(root, sdk) })
  }
  // Re-open chat realtime for the NEW workspace when the user is on a chat
  // surface — the teardown at the top closed the old stream, and the chat
  // page only wires the subscription in onCreate (no remount on switch).
  // subscribeRealtime is workspace-keyed + idempotent, so a racing call
  // from the page is harmless. Dynamic import avoids a static cycle.
  if (chatSurfaceActive) {
    import('@symbo-ls/workspace-chat/functions/subscribeRealtime.js')
      .then((m) => m.subscribeRealtime.call({ state: { root } }))
      .catch(() => {})
  }
  let nextWorkspace = null
  if (activeWsId && typeof sdk.getWorkspace === 'function') {
    try {
      const ws = await _phase('workspace-record', () => getWorkspaceCached(sdk, activeWsId))
      // Cross-org stale-workspace guard. `root.org` updates reliably on a switch;
      // only adopt the fetched record when it BELONGS to the active org, so a
      // stale id that resolves to another org's workspace can never leak its
      // skin/navbar/settings in. Mismatch → keep the previous record.
      const orgId = root.org?.id || root.org?._id
      const wsOrg = ws?.organization || ws?.organizationId || ws?.org
      if (ws && (!orgId || !wsOrg || String(wsOrg) === String(orgId))) {
        // getWorkspaceCached always returns the full server record — see
        // tickets/spec-workspace-record-shape.md.
        nextWorkspace = shellStampWorkspaceShape(ws, 'full', { clone: false })
      } else if (ws) {
        console.warn('[workspace-project] workspace-change: ignoring cross-org workspace', {
          wsOrg,
          orgId,
          ws: ws?.displayName || ws?.name
        })
      }
    } catch (e) {
      console.warn('[workspace-project] workspace-change: getWorkspace refetch failed', e)
    }
  }

  // BARE ORG SWITCH resolution (org picked without a workspace target — the
  // switcher's org row, sdk.switchOrg, a /w/<other-org> deep link): the claims
  // and sdk-context still carry the PREVIOUS org's workspace until the token
  // re-mint lands, so `activeWsId` either resolved to a foreign-org record
  // (rejected by the guard above — correctly) or to nothing. The session then
  // sat workspace-less — no module, no data, no URL — until a later auth tick
  // ran the workspaces-list fallback, which is exactly the "switch to an org
  // and it half-loads" flavor of the isolation bug. Resolve the NEW org's
  // list right here and default to its HQ, so every org switch lands
  // deterministically in the same pass.
  if (!nextWorkspace && root.org && typeof sdk.listWorkspaces === 'function') {
    const newOrgId = String(root.org.id || root.org._id || '')
    const staleWs = String(activeWsId || '') // may be the previous org's ws
    try {
      const res = await _phase('org-workspaces-list', () =>
        sdk.listWorkspaces({ organization: newOrgId })
      )
      const rows =
        (res && (res.items || res.data?.items || res.data || res.workspaces || res)) || []
      const list = Array.isArray(rows) ? rows : []
      // The tab's URL may NAME the target workspace — scheme A path segment 2
      // (`/w/<org>/<handle>/…`) or a legacy `?ws=` deep link. That's the
      // user's EXPLICIT intent and beats every default below; without it a
      // cross-org `?ws=` deep link reconciled the org fine but then landed on
      // the org's HQ, silently dropping the named workspace. Matched
      // slug → key → id against the freshly-listed rows (the list is already
      // org-scoped by the query, so no org filter needed here).
      const urlIntent = (() => {
        try {
          if (typeof window === 'undefined' || !window.location) return null
          const { orgSlug, seg2 } = shellGetWorkspaceRawSegments(window.location.pathname || '')
          const rootSlug = root.org?.slug || root.org?.username || null
          const fromPath = orgSlug && rootSlug && orgSlug === rootSlug ? seg2 : null
          return fromPath || shellGetWorkspaceQueryParam(window.location.search || '') || null
        } catch {
          return null
        }
      })()
      const wantH = urlIntent ? String(urlIntent).toLowerCase() : null
      const eqH = (v) => String(v ?? '').toLowerCase() === wantH
      const intent = wantH
        ? list.find((w) => w?.slug && eqH(w.slug)) ||
          list.find((w) => w?.key && eqH(w.key)) ||
          list.find((w) => eqH(w?._id || w?.id))
        : null
      const hq =
        intent ||
        list.find((w) => String(w?._id || w?.id || '') === staleWs) ||
        list.find((w) => w?.type === 'hq') ||
        list[0]
      const hqId = hq ? String(hq._id || hq.id || '') : ''
      if (hqId && !superseded()) {
        // URL-first: the LIST row already carries this switch's identity
        // (slug/key/id + org), and the address bar needs nothing more. Telling
        // the caller here — before the full-record fetch below — cuts a whole
        // round trip off the window in which the URL still names the OLD org
        // (measured live: this bare-org arm is the SLOW one, two RTTs to
        // adoption). Org-verified with the same check the adoption uses, so a
        // foreign row can never be stamped.
        const hqOrg = hq?.organization || hq?.organizationId || hq?.org || hq?.org_id
        if (!hqOrg || String(hqOrg) === newOrgId) notifyWorkspaceResolved(hq)
        // Full record (settings bag — the module ref lives there); fall back
        // to the lean row when the fetch fails so the pass still scopes. The
        // `listWorkspaces` row (`hq`) is stamped `list` even though the wire
        // payload can carry `settings` too — this code path already treats it
        // as a fallback, not the authoritative fetch; keeping the stamp
        // conservative means a failed `full` fetch still gets picked up by
        // `setActiveWorkspaceRecord`'s auto-retry instead of being trusted as
        // complete. See tickets/spec-workspace-record-shape.md.
        let full = null
        try {
          const raw = await _phase('hq-record', () => getWorkspaceCached(sdk, hqId))
          full = raw ? shellStampWorkspaceShape(raw, 'full', { clone: false }) : null
        } catch (_) {
          /* lean fallback below */
        }
        const rec = full || shellStampWorkspaceShape(hq, 'list')
        const recOrg = rec?.organization || rec?.organizationId || rec?.org
        if (!recOrg || String(recOrg) === newOrgId) {
          nextWorkspace = rec
          // Mirror the resolved scope everywhere the immediate path does
          // (sdk context, per-tab pointer, fetch scope) — the claims tick
          // converges later and finds itself in agreement.
          try {
            sdk.updateContext?.({ activeWorkspaceId: hqId })
          } catch (_) {
            /* context mirror is best-effort */
          }
          shellSetActiveWorkspacePointer(hqId)
          put({ activeWorkspaceId: hqId, __fetchScope: `${newOrgId}.${hqId}` })
        }
      }
    } catch (e) {
      console.warn('[workspace-project] workspace-change: org workspaces resolve failed', e)
    }
  }

  // A newer switch started while the workspace record was in flight — abandon
  // before adopting/refetching so late writes can't clobber the newer switch.
  if (superseded()) return

  // Adopt the freshly-fetched workspace record POST-wipe (PHASE 1 ran EARLY,
  // right after the snapshots — see above): applyWorkspaceConfig below must
  // resolve THIS workspace's Mongo settings. On fetch failure the previous
  // record stays — never blank the active workspace. Guarded setter (not
  // `put({ workspace: ... })`) so a list-shaped fallback row can never
  // downgrade a full record already held for this id, and so a genuinely
  // list-shaped adoption schedules its own full-record fetch — see
  // tickets/spec-workspace-record-shape.md. `superseded()` mirrors `put`'s
  // own guard since the setter writes `root.update` directly.
  if (nextWorkspace && !superseded()) {
    shellSetActiveWorkspaceRecord(root, nextWorkspace, {
      reason: 'onWorkspaceChange',
      sdk,
      getWorkspaceCached
    })
  }
  // The switch's identity is resolved NOW — tell the caller (see the
  // `onWorkspaceResolved` note on this function). `root.workspace` is read
  // back rather than `nextWorkspace` passed blind, so a rejected downgrade
  // (setActiveWorkspaceRecord's list-over-full guard) hands out the record
  // actually held. A null resolve still calls back: the handler's own
  // validator decides, and a no-candidate pass simply no-ops there.
  notifyWorkspaceResolved(root.workspace || nextWorkspace || null)

  // Re-scope the SHELL-LEVEL meet realtime channel to the new workspace
  // (isolation audit #7). The shell subscribes once at mount (app.js — the
  // OngoingCallBadge feed) and nothing re-invoked it on a switch, so the old
  // workspace's channel kept streaming into root.rooms/liveParticipants. The
  // subscription is workspace-keyed + re-entrant since 245f9761 — this call
  // tears the stale channel down and opens the new workspace's. Runs AFTER
  // the replace above so its workspace-first scope resolver reads the NEW
  // record. Always (not surface-gated): the badge is shell chrome. Same
  // element-shim calling convention as the chat re-open above.
  import('@symbo-ls/workspace-meet/functions/subscribeMeetRealtime.js')
    .then((m) =>
      m.subscribeMeetRealtime.call(
        { state: { root } },
        { tables: ['meet_rooms', 'meet_participants'] }
      )
    )
    .catch(() => {})
  // Files desktop realtime — page-level channel; re-scope only when it exists
  // (or the user is sitting on /files). subscribeFilesRealtime tears down a
  // cross-workspace channel and resubscribes under the active one (245f9761).
  if (root._filesRealtimeUnsub || _path === '/files' || _slot === '/files') {
    import('@symbo-ls/workspace-files/functions/subscribeFilesRealtime.js')
      .then((m) => m.subscribeFilesRealtime.call({ state: { root } }))
      .catch(() => {})
  }

  // Dashboard prefs for the NEW workspace — started now (post-wipe, so the
  // replace above can't clobber the landed row) and AWAITED in the essential
  // set below: the home grid re-converges from this row, so revealing before
  // it lands re-shuffles the dashboard in front of the user (the boot bounce,
  // switch flavor — 2026-07-27 per Nika). Pinned to activeWsId explicitly
  // (root.workspace keeps the PREVIOUS record when the refetch above failed)
  // and routed through the epoch-guarded `put` so a superseded switch's late
  // row never repaints the newer switch's dashboard.
  const _switchErrors = []
  updateWorkspaceActivation(root, 'core', { state: 'loading' })
  const _prefsPromise = Promise.all(
    _dashboardPrefsFetches(root, sdk, {
      tag: 'workspace-change: ',
      workspaceId: activeWsId,
      write: put,
      onError: (phase) => _switchErrors.push(phase)
    })
  )
  // Keep the completion path attached after the reveal budget expires. A
  // pending roster/team read is neither confirmed empty nor disposable.
  const _essentialsWork = Promise.all([_peoplePromise, _teamsPromise, _prefsPromise]).then(
    ([_peopleData, _teamsRes]) => {
      if (superseded()) return
      if (Array.isArray(_peopleData)) {
        const all = _peopleData.map(_person)
        const _people = filterToActiveOrg(all, root)
        put({
          peopleAll: all,
          people: _people,
          peopleLoaded: true,
          birthdays: _deriveBirthdays(_people)
        })
      }
      if (_teamsRes) {
        const { teams = [], teamsByEmail = {}, orgMembersByOrgId = {} } = _teamsRes
        put({ teams, teamsByEmail, orgMembersByOrgId })
      }
    }
  )

  // Unpack the NEW workspace's project (skin/navbar/panels/apps + records) into
  // context, THEN apply its config. On a switch this runs AFTER the replace()
  // above reset root.workspaceSettings AND repointed root.workspace at the new
  // record, so the previous workspace's project + skin are cleared and the new
  // one's are applied — the deactivate-old / activate-new cycle. A workspace with
  // no project (and no Mongo settings) reverts to the framework default.
  //
  // BOUNDED (WS-SWITCH-ONWORKSPACECHANGE-BOUNDED-WINDOW-TRIP-1): the module
  // load is the one pre-reveal phase that legitimately runs for many seconds —
  // it fetches the published module and its chunks, and a COLD dev chunk fetch
  // alone outran the whole 10s watchdog window on a Bellforge activation (see
  // the prewarm note in workspaceProjects.js). Waiting for it is a preference
  // (reveal on a fully-applied module), never a requirement: the loader must
  // not hang on it. Past the budget the pass reveals with the workspace data it
  // already has, and the module applies itself when it lands — the same
  // epoch/staleness guards the loaders already enforce.
  //
  // The page the person waits for is the takeover plus the app the URL names
  // (whenAppRouteReady; the home needs none). Config applies at that gate, as
  // on boot, and again once every app has published. `_moduleWork` still
  // covers every app for the final ready/error, and the loader still waits
  // for it up to the budget.
  let _releaseModuleReveal = () => {}
  const _moduleReveal = new Promise((resolve) => {
    _releaseModuleReveal = resolve
  })
  const _moduleWork = (async () => {
    await loadWorkspaceProject(root, sdk)
    if (superseded()) return
    const _appsRun = loadWorkspaceApps(root, sdk)
    whenAppRouteReady(root).then(() => {
      if (!superseded()) {
        try {
          applyWorkspaceConfig(root)
        } catch (e) {
          console.warn(
            '[workspace-project] workspace-change: applyWorkspaceConfig (reveal) failed',
            e
          )
        }
      }
      _releaseModuleReveal()
    })
    await _appsRun
    if (superseded()) return
    applyWorkspaceConfig(root)
  })()
  _moduleWork.then(_releaseModuleReveal, _releaseModuleReveal)
  // The notice speaks for that page and the essential set, never for apps
  // still setting up in the background (boot fix step 2). It waited for every
  // app, so a switch into a workspace with many apps said "still loading" over
  // a ready home until the last app answered. 'slow' is now written only while
  // this set is out, and steps back once it lands.
  let _revealSetIn = false
  const _revealSet = Promise.all([_moduleReveal, _essentialsWork])
  _revealSet.then(
    () => {
      _revealSetIn = true
    },
    () => {}
  )
  Promise.all([_moduleWork, _essentialsWork]).then(
    () => {
      if (!superseded()) {
        updateWorkspaceActivation(root, 'core', {
          state: _switchErrors.length ? 'error' : 'ready',
          failed: [...new Set(_switchErrors)]
        })
      }
    },
    (error) => {
      console.warn('[workspace-project] workspace-change: activation failed', error)
      if (!superseded()) updateWorkspaceActivation(root, 'core', { state: 'error' })
    }
  )
  const _moduleRow = await _phase('module-activate', () => _moduleWork, _BOUNDED)

  // A newer switch superseded this one during the workspace/module resolve —
  // stop before writing/revealing so its late writes never repaint stale data.
  if (superseded()) return

  // ESSENTIAL set — await the roster + teams (both already in flight from the
  // top, so this resolves the moment the module load above finishes) plus the
  // new workspace's dashboard-prefs row, and put them post-wipe
  // (epoch-guarded). These are what the UI reveals with.
  const _essentialRow = await _phase('essential-set', () => _essentialsWork, _BOUNDED)
  if ((_moduleRow === _BOUNDED || _essentialRow === _BOUNDED) && !_revealSetIn && !superseded()) {
    const _slowStamp = updateWorkspaceActivation(root, 'core', { state: 'slow' })
    _revealSet.then(
      () => {
        if (!superseded()) {
          settleWorkspaceActivation(root, 'core', _slowStamp, { state: 'loading' })
        }
      },
      () => {}
    )
  }

  // REVEAL — clear the switch loader now that the essential data landed (only the
  // current, non-superseded switch clears it — endDataLoad's epoch check is the
  // same invariant for the flag itself). Everything else fills in behind the
  // revealed UI via the non-blocking tail below.
  if (superseded()) return
  // The switch flags are THIS pipeline's to clear even when a newer
  // _dataLoading raise owns the flag itself (endDataLoad → false): deliver
  // them via the epoch-guarded put so the loader can't wedge on _wsSwitching.
  const _revealPatch = { _wsSwitching: null, _pendingActiveOrgId: null }
  if (!endDataLoad(root, _dlEpoch, _revealPatch)) put(_revealPatch)
  // Reached the reveal normally — the watchdog above is now moot; clearing it
  // (rather than relying solely on its own no-op check) means a genuinely slow
  // but successful switch never risks a spurious loud error landing after the
  // fact.
  clearTimeout(_wsChangeWatchdog)
  // A synchronous state subscriber can start another switch during reveal.
  if (superseded()) return

  // NON-BLOCKING TAIL (P1) — the remaining workspace-scoped refetches, fired like
  // boot's fan-out (void Promise.all, NOT awaited): each self-hydrates via the
  // epoch-guarded put, nothing downstream reads them synchronously, so they fill
  // in behind the already-revealed UI instead of pinning the loader on the
  // slowest of them (chat.messages limit 500 / calendar limit 2000 on a cold
  // backend was the 8-10s switch).
  //
  // The tickets entries below read through an ELEMENT: every tickets loader
  // starts with `el.call('getSDK')` (the frank-safe call), and the root STATE
  // has no `call`, so they take the state's back-pointer element (FA001; the
  // same handle `disconnectCall` uses above). Handed the state, every read
  // failed at once ("el.call is not a function") and My tickets and the
  // activity feed came back empty after each switch.
  const _ticketsEl = root.__element
  void Promise.all([
    // Member→team mapping + member→role mapping — the essential set above put
    // `teamsByEmail: {}` (loadAllTeams always returns it empty — the per-team
    // fetch is deferred, same as cold boot) and never refreshed `root.roles`
    // at all. Cold boot's fan-out chains loadAllTeamMembers after loadAllTeams
    // resolves and calls _loadRoles once; this switch path did NEITHER, so
    // after ANY org/workspace switch, /people and /admin permanently grouped
    // every member under "No team"/"Unassigned" (teamsByEmail never filled in
    // for the new org) and role badges kept showing the PREVIOUS org's
    // member-role mapping (`root.roles.byUser` is keyed by the global userId,
    // not scoped per-org, and nothing re-ran the fetch) — see tickets/
    // sonnet.md TEAM-GROUPING-1 / ROLE-FLIP-1. `loadAllTeamMembers` no-ops
    // safely if `root.teams` isn't populated yet (guarded internally).
    _essentialsWork
      .then(() => {
        if (!superseded()) return loadAllTeamMembers(_teamRoot)
      })
      .then(() => put({ teamsByEmailLoaded: true }))
      .catch((e) => console.warn('[workspace-project] workspace-change: team members failed', e)),
    _loadRoles(root, sdk).catch((e) =>
      console.warn('[workspace-project] workspace-change: member-roles fetch failed', e)
    ),
    sdk
      .execute('workspaceProject.announcements', 'list', { order: 'date' })
      .then((data) => {
        if (!data) return
        put({
          announcements: data.map((r) => ({
            id: r.id,
            title: r.title,
            author: r.author,
            // Same split as _person's avatar: URL-or-empty, letters in
            // their own field.
            authorAvatar:
              typeof r.author_avatar === 'string' && /^(https?:|\/)/.test(r.author_avatar)
                ? r.author_avatar
                : '',
            authorInitials: initials(r.author),
            authorRole: r.author_role,
            date: r.date,
            readTime: r.read_time,
            tag: r.tag,
            cover: r.cover,
            excerpt: r.excerpt,
            body: r.body,
            reactions: r.reactions || {},
            audience: r.audience === 'external' ? 'external' : 'internal',
            externalUrl: r.external_url || r.externalUrl || ''
          }))
        })
      })
      .catch((e) =>
        console.warn('[workspace-project] workspace-change: announcements fetch failed', e)
      ),
    (() => {
      const min = new Date(Date.now() - 1 * 86400000).toISOString()
      const max = new Date(Date.now() + 14 * 86400000).toISOString()
      return sdk
        .execute('workspaceProject.calendar', 'list', {
          filter: { deleted: false, start_at: { gte: min, lte: max } },
          order: 'start_at',
          limit: 500
        })
        .then((data) => {
          if (!data) return
          const me = root.currentUserEmail || ''
          const byKey = new Map()
          for (const r of data) {
            const key = r.google_event_id || `local-${r.id}`
            const existing = byKey.get(key)
            if (!existing) {
              byKey.set(key, r)
              continue
            }
            if (r.google_calendar_id === me && existing.google_calendar_id !== me) {
              byKey.set(key, r)
            }
          }
          put({
            intranetEvents: [...byKey.values()].map((r) => ({
              id: r.id,
              title: r.title,
              startAt: r.start_at,
              endAt: r.end_at,
              location: r.description ? r.description.slice(0, 60) : '',
              // Carry the FULL description + invitees through so the home
              // "Coming up" event modal shows them directly. The modal's
              // lazy fetchEventDetails 404s on home (no GET
              // /calendar/events/:id route — only `list` exists) and
              // root.events is empty here, so this seed is the only source
              // (2026-06-08 per Nika "data not loading in Coming up event
              // modals"). The list response already includes both fields.
              description: r.description ?? null,
              invitees: Array.isArray(r.invitees) ? r.invitees : [],
              attendees: Array.isArray(r.invitees) ? r.invitees : [],
              organizerEmail: r.organizer_email,
              googleEventId: r.google_event_id
            })),
            intranetEventsLoaded: true,
            intranetEventsError: false
          })
        })
        .catch((e) => {
          console.warn('[workspace-project] workspace-change: calendar_events fetch failed', e)
          put({ intranetEventsError: true })
        })
    })(),
    // intranet `stories` removed — dead boot fetch (StoriesWidget deleted
    // 2026-06-02, no root.stories consumer). See 0162_drop_dead_stories.sql.
    // resource_links migrated to Mongo at /resource-links/*.
    sdk
      .execute('resourceLinks', 'list', {})
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || []
        if (data?.length) put({ resourceLinks: data })
      })
      .catch((e) =>
        console.warn('[workspace-project] workspace-change: resource_links fetch failed', e)
      ),
    // user_documents migrated to Mongo as `docs` with type='user_document'.
    shellIsCommHost()
      ? null
      : sdk
          .execute('docs.userDocs', 'list', {})
          .then((res) => {
            const data = Array.isArray(res) ? res : res?.data || []
            if (data?.length) put({ userDocs: data })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: user_documents fetch failed', e)
          ),
    // (homeDashboardPrefs moved OUT of this tail into the awaited essential
    // set above — the reveal must show the new workspace's real dashboard.)
    fetchOrgNotifCount(root).catch((e) =>
      console.warn('[workspace-project] workspace-change: org-notifications fetch failed', e)
    ),
    // Tickets re-fetches — only fire when the corresponding state slot
    // was populated BEFORE the wipe (i.e., user is currently on or has
    // visited a tickets page this session). The `had*` snapshot is
    // captured at the top of this function before `root.replace` clears
    // these slots; checking the post-wipe `root.X` directly would always
    // return empty and skip the refetches. On a cold mount of /tickets
    // the page's own onCreate handles the first fetch.
    //
    // The BOARD itself is not reloaded here: the board page's own loader
    // (tickets pages/board.js `_loadBoard`, sequence-guarded) watches
    // `workspace` and every filter key and reloads on a real switch, and a
    // board that is not mounted loads on its next mount. The tail used to run
    // a second board reload (reloadTicketsForRoot) that wrote through
    // root.update, outside the epoch-guarded put and outside the board's
    // sequence guard. Since 819543f10 it failed before its first read and
    // then wrote empty counts and an empty board over the page's own wave; a
    // working copy would race that wave, and after a quick double switch could
    // paint the previous workspace's rows. So the tail only re-seeds the prefs.
    hadTicketsBoard || ticketsSurfaceActive
      ? Promise.resolve()
          .then(() => {
            // Board prefs went per-workspace (245f9761): the wipe re-armed
            // the seed latch, so re-seed from the NEW workspace's storage
            // blob before the board repaints (a mounted board never
            // remounts, so nothing else would). The seed's filter writes and
            // the new `workspace` record start the board's own reload.
            // el.call through the root element (see `_ticketsEl` above): a
            // bare call of the library import as its own statement shipped
            // unresolved in the frank payload.
            if (_ticketsEl && typeof _ticketsEl.call === 'function') {
              _ticketsEl.call('seedTicketsBoardPrefs', root)
            }
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: tickets prefs seed failed', e)
          )
      : Promise.resolve(),
    hadMyTickets && root.currentUserEmail
      ? fetchTicketsAssignedTo(_ticketsEl, root.currentUserEmail, 100)
          .then((rows) => put({ myTickets: rows }))
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: myTickets fetch failed', e)
          )
      : Promise.resolve(),
    hadFeedItems
      ? fetchActivityFeed(_ticketsEl, { limit: 80 })
          .then((items) => put({ feedItems: items }))
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: feedItems fetch failed', e)
          )
      : Promise.resolve(),
    // Documents / files desktop (isolation audit #2) — these keys joined the
    // wipe registry; the same had-or-active convention refetches them for the
    // NEW workspace so a mounted surface never strands blank (the fetch
    // plugin is create-only). Reloaders live in the OWNING packages and reuse
    // the pages' declarative transforms; dynamic import mirrors the chat
    // subscribeRealtime pattern (no static cross-package cycle).
    hadDocuments || documentsSurfaceActive || homeSurfaceActive
      ? import('@symbo-ls/workspace-documents/functions/index.js')
          .then((m) => m.reloadDocsAfterSwitch(root, sdk, put))
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: documents reload failed', e)
          )
      : Promise.resolve(),
    hadFileNodes || filesSurfaceActive
      ? import('@symbo-ls/workspace-files/functions/fetchConfigs.js')
          .then((m) => m.reloadFilesAfterSwitch(root, sdk, put))
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: files reload failed', e)
          )
      : Promise.resolve(),
    // Credit guard — wiped by the registry (workspace-scoped balance);
    // refetch UNCONDITIONALLY: the blocker/banner are policy gates that must
    // re-evaluate for the NEW tenant whether or not the old one had data.
    loadCreditGuard(root, sdk).catch((e) =>
      console.warn('[workspace-project] workspace-change: credit guard fetch failed', e)
    ),
    // userPrefs was wiped by the registry but never refetched on a switch
    // (the inverse leak — preferences went BLANK after every switch; audit
    // runners-up). Same call the boot chain makes.
    hadUserPrefs
      ? sdk
          .execute('workspaceProject.userPreferences', 'get')
          .then((data) => {
            // No row (a new account) is a loaded, empty row — see the boot read.
            put({ userPrefs: data && typeof data === 'object' ? data : {} })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: user_preferences fetch failed', e)
          )
      : Promise.resolve(),
    // companyInfo joined the wipe registry (org financial identity must not
    // bleed across orgs) — re-derive from the NEW workspace's settings bag
    // when the surface used it (root.workspace was repointed by the replace
    // above, so this reads the fresh record).
    hadCompanyInfo || companySurfaceActive
      ? Promise.resolve().then(async () => {
          // §5 RENDER site. Unlike the boot path above, this one had no fetch
          // fallback at all — it is a post-switch re-derive off whatever record
          // the switch just adopted. When that adoption was the LEAN fallback
          // row (onWorkspaceChange's `full || stampWorkspaceShape(hq, 'list')`
          // path), `settings.companyInfo` was `undefined` and this published
          // `[]` — rendering "this org has no company info" over an org that
          // has some, permanently: nothing re-derives companyInfo later, and
          // §6's converge covers the config chain, not this row.
          //
          // So mirror the boot path and complete the read. `getWorkspaceCached`
          // is the same coalescer §4c's auto-fetch rides, so this joins that
          // in-flight request rather than adding a second one.
          const { settings: heldSettings, ready, id: wsId } = shellWorkspaceSettings(root)
          let settings = ready ? heldSettings : null
          if (!settings && wsId && typeof sdk.getWorkspace === 'function') {
            try {
              settings = (await getWorkspaceCached(sdk, wsId))?.settings
            } catch (e) {
              console.warn('[workspace-project] workspace-change: company-info fetch failed', e)
            }
          }
          const info = settings?.companyInfo
          const rows = Array.isArray(info) ? info : info ? [info] : []
          put({ companyInfo: rows })
        })
      : Promise.resolve(),
    // Chat — same conditional-refetch convention as tickets above.
    // Only fire when the user was previously on /chat (`channels` is
    // populated by the page's mount-time fetch declarations). A cold
    // mount of /chat later still re-fetches via the page's own
    // `fetch: [...]` blocks; this only keeps the in-memory cache
    // consistent for users currently looking at the chat surface
    // when the switch happened.
    hadChannels || chatSurfaceActive || homeSurfaceActive
      ? sdk
          .execute('workspaceProject.chat', 'list')
          .then((data) => {
            if (!data) return
            const list = Array.isArray(data) ? data : data?.data || []
            // Scope to the NEW workspace — the server list is the
            // workspace ∪ membership union; writing it verbatim right
            // after the PHASE 1 wipe restored the cross-tenant rows.
            put({
              channels: filterChannelsToActiveWorkspace(
                list.map(rowToChannel),
                getActiveWorkspaceId(root)
              )
            })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: chat.channels fetch failed', e)
          )
      : Promise.resolve(),
    hadChannels || chatSurfaceActive
      ? sdk
          .execute('workspaceProject.chat.members', 'list')
          .then((data) => {
            if (!data) return
            const list = Array.isArray(data) ? data : data?.data || []
            put({ channelMembers: scopeMembersToActiveWorkspace(list, root) })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: chat.members fetch failed', e)
          )
      : Promise.resolve(),
    // Mentions were wiped in PHASE 1 (myMentions) but never refetched —
    // badges stayed empty until a full /chat remount. Refetch alongside
    // the other chat slices, scoped to the new workspace.
    hadChannels || chatSurfaceActive
      ? sdk
          .execute('workspaceProject.chat.mentions', 'list', {
            options: { order: 'created_at' }
          })
          .then((data) => {
            if (!data) return
            const rows = Array.isArray(data) ? data : data?.data || []
            put({ myMentions: rows.filter((r) => rowInActiveWorkspace(root, r)) })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: chat.mentions fetch failed', e)
          )
      : Promise.resolve(),
    hadChatMessages || chatSurfaceActive
      ? sdk
          // `ascending: false` used to sit alongside `order` here. Dropped
          // 2026-09-22: this endpoint ignores sorting options altogether
          // (verified against dev.api.symbols.app — see chat/functions/collab/
          // queries.js `listMessages` for the full account) and always answers
          // oldest row first. Keeping a dead key around invites the next reader
          // to trust it.
          .execute('workspaceProject.chat.messages', 'list', {
            options: { limit: 500, order: 'created_at' }
          })
          .then((data) => {
            if (!data) return
            const rows = Array.isArray(data) ? data : data?.data || []
            const byCh = {}
            for (const r of rows) {
              // Workspace-scope each row — the bulk read spans every
              // RLS-visible channel across workspaces.
              if (!rowInActiveWorkspace(root, r)) continue
              const m = rowToMessage(r)
              if (!byCh[m.channelId]) byCh[m.channelId] = []
              byCh[m.channelId].push(m)
            }
            // Sort oldest → newest, the one order ChatMessageList renders.
            // This `put` writes the whole container, bypassing chatUtils
            // `setChannelMessages`, so it has to apply the invariant itself.
            // The `.reverse()` it replaces assumed the server answered
            // newest-first; it answers oldest-first, so the reverse was
            // actively creating the inverted channel Jerry reported.
            for (const k in byCh) byCh[k] = sortMessagesByTime(byCh[k])
            put({ messagesByChannel: byCh })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: chat.messages fetch failed', e)
          )
      : Promise.resolve(),
    // AI Assistant threads — same conditional-refetch convention as chat
    // above (only when the user had threads, or the panel was open, in
    // the previous workspace). Through the root element's el.call (the
    // registered aiChatListThreads; this = root element), NOT a relative
    // `import('./aiAssistantThreads.js')`: frank cannot rebuild a
    // project-local dynamic import, so the published switch threw on the
    // bundler's leftover export wiring and never refetched
    // (shellPublishedEmission.test.js). `root.activeWorkspaceId` was
    // already mirrored from `sdk._context.activeWorkspaceId` above, so the
    // SDK call scopes to the workspace actually switched into.
    hadAiThreads
      ? Promise.resolve()
          .then(() => {
            const hostEl = root.__element
            if (hostEl && typeof hostEl.call === 'function')
              return hostEl.call('aiChatListThreads', root)
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: aiChat threads fetch failed', e)
          )
      : Promise.resolve(),
    // SIMONE conversation core — PHASE-2 pair for the bucket-E drain
    // (WS-SWITCH-INCOMPLETE-1 step 8). `hydrateServerSessions` is the SAME
    // function Chat.onCreate runs on mount; it pins the AI service to the
    // active workspace itself (pinWorkspace), so it lists the NEW workspace's
    // member conversations. Runs on the root element via `el.call` (this =
    // root element, `state.root` = root) — `root.__element` is the state's
    // own back-pointer, no fake host. General scope only: the add-app thread
    // (`_ax_*`) hangs off `_addAppProjectId`, which the wipe nulls, and
    // re-hydrates when the page selects an extension again.
    hadSimoneSessions
      ? Promise.resolve()
          .then(() => {
            const hostEl = root.__element
            if (hostEl && typeof hostEl.call === 'function')
              return hostEl.call('hydrateServerSessions', 'general')
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: simone sessions hydrate failed', e)
          )
      : Promise.resolve(),
    // ASK-USER decision rows — same pair. `fetchAskUserDecisions` RETURNS the
    // filtered rows (it does not write root), so `put` lands them
    // epoch-guarded like every other PHASE-2 slice. Dynamic import for the
    // same static-cycle reason as aiAssistantThreads above.
    hadAskUserDecisions
      ? import('./aiAssistant.js')
          .then(({ fetchAskUserDecisions }) => fetchAskUserDecisions(root))
          .then((rows) => {
            if (Array.isArray(rows)) put({ askUserDecisions: rows })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: ask-user decisions fetch failed', e)
          )
      : Promise.resolve(),
    // Calendar / Meet page caches — same shape as chat above. Only
    // refetched when the user was on /calendar or /meet (page's own
    // `fetch: [...]` declaration only fires on mount).
    //
    // CALENDAR-PAGE-CACHE-WINDOW (2026-05-22): the previous code called
    // `sdk.execute('workspaceProject.calendar', 'list')` with NO args
    // and wrote the raw response straight into `root.events`. Two bugs
    // compounded:
    //   1. Without filter + limit, the server returned its default page
    //      (1000 oldest-first rows by start_time ASC) — the cache write
    //      CLOBBERED the page's own filtered ±14d fetch with March-2026
    //      data that contained nothing in the current week.
    //   2. The raw Supabase rows use snake_case (`google_calendar_id`,
    //      `start_at`, ...) but the calendar grid views filter on the
    //      camelCase shape `rowToEvent` produces (`googleCalendarId`,
    //      `startAt`, ...). Even when a row matched the current week,
    //      the filter never matched the user's email.
    // Mirror the page declaration: same window, same limit, and shape
    // every row through the same camelCase mapping as `rowToEvent` in
    // the calendar page. Inlined here (not imported) because the
    // workspace-shell must not depend on the calendar package.
    hadCalendarEvents || calendarSurfaceActive
      ? (() => {
          const min = new Date(Date.now() - 14 * 86400000).toISOString()
          const max = new Date(Date.now() + 14 * 86400000).toISOString()
          return sdk
            .execute('workspaceProject.calendar', 'list', {
              filter: { deleted: false, start_at: { gte: min, lte: max } },
              order: 'start_at',
              limit: 2000
            })
            .then((data) => {
              if (!data) return
              const list = Array.isArray(data) ? data : data?.data || []
              const mapped = list.map((r) => ({
                id: r.id,
                title: r.title,
                date: r.date,
                startTime: r.start_time,
                endTime: r.end_time,
                endDate: r.end_date,
                startAt: r.start_at || null,
                endAt: r.end_at || null,
                color: r.color,
                type: r.type || 'meeting',
                description: r.description,
                invitees: r.invitees,
                reminderMinutes: r.reminder_minutes ?? null,
                creatorEmail: r.organizer_email,
                googleEventId: r.google_event_id,
                googleCalendarId: r.google_calendar_id
              }))
              put({ events: mapped })
            })
            .catch((e) =>
              console.warn('[workspace-project] workspace-change: calendar.events fetch failed', e)
            )
        })()
      : Promise.resolve(),
    // Profile page — re-resolve the viewed user against the NEW tenant's
    // roster and refetch its activity heatmap + public projects. `put` is the
    // epoch-guarded writer, so a superseded switch drops its patch exactly
    // like every other PHASE-2 slice. The roster (`peopleAll`) is re-put by
    // this same pass; `hydrateUserProfile` re-fetches it itself when it is
    // still empty at that moment, so the two orders both resolve.
    profileSurfaceActive
      ? hydrateUserProfile(root, { write: put }).catch((e) =>
          console.warn('[workspace-project] workspace-change: user profile rehydrate failed', e)
        )
      : Promise.resolve(),
    // History app — the log slice was wiped in PHASE 1 and the page's own read
    // ran once, at mount. Read the NEW workspace's log through the caller's SDK
    // and the epoch-guarded `put`, so a superseded switch never lands its rows
    // (functions/history.js loadHistoryEvents also drops a reply that outlived
    // its workspace).
    historySurfaceActive
      ? loadHistoryEvents(root, { sdk, write: put }).catch((e) =>
          console.warn('[workspace-project] workspace-change: history reload failed', e)
        )
      : Promise.resolve(),
    // Getting started → Team — the NEW workspace's found people, through the
    // caller's SDK and the epoch-guarded `put` (the loader also drops a reply
    // that outlived its workspace).
    gettingStartedSurfaceActive
      ? loadGettingStartedTeam(root, { sdk, write: put }).catch((e) =>
          console.warn(
            '[workspace-project] workspace-change: getting-started team reload failed',
            e
          )
        )
      : Promise.resolve(),
    // Getting started → Imports — the NEW workspace's found sources, the
    // same way.
    gettingStartedImportsActive
      ? loadGettingStartedImports(root, { sdk, write: put }).catch((e) =>
          console.warn(
            '[workspace-project] workspace-change: getting-started imports reload failed',
            e
          )
        )
      : Promise.resolve(),
    hadMeetRooms || meetSurfaceActive
      ? sdk
          .execute('workspaceProject.meet.rooms', 'list')
          .then((data) => {
            if (!data) return
            const list = Array.isArray(data) ? data : data?.data || []
            put({ rooms: list })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: meet.rooms fetch failed', e)
          )
      : Promise.resolve(),
    hadMeetWaitingRoom
      ? sdk
          .execute('workspaceProject.meet.waitingRoom', 'list')
          .then((data) => {
            if (!data) return
            const list = Array.isArray(data) ? data : data?.data || []
            put({ waitingRoom: list })
          })
          .catch((e) =>
            console.warn('[workspace-project] workspace-change: meet.waitingRoom fetch failed', e)
          )
      : Promise.resolve()
  ])
}

export async function retryWorkspaceActivation() {
  const root = this.state?.root || this.state
  if (!root || root.signedIn === false) return
  const generation = scopeGen()
  const workspaceId = String(root.workspace?.id || root.workspace?._id || '')
  try {
    const sdk = this.sdk || _wsShared.functions.getSDK()
    return await loadPrivateData(root, sdk, { background: true })
  } catch (error) {
    console.warn('[workspace-project] activation retry failed', error)
    if (
      !scopeStale(generation) &&
      String(root.workspace?.id || root.workspace?._id || '') === workspaceId
    ) {
      updateWorkspaceActivation(root, 'core', { state: 'error' })
    }
  }
}
